/**
 * API Service Layer for ABS ERP System
 * 
 * This file contains all API endpoint definitions and helper functions
 * for integrating with the Django REST API backend.
 * 
 * IMPORTANT: All API calls are currently COMMENTED OUT for development.
 * Uncomment when Django backend is ready.
 */

import {
  normalizeReporteVentasGeneral,
  normalizeReporteVentasPorItem,
  normalizeReporteVentasPorVendedor,
  normalizeReporteVentasPorUsuarioDetalle,
  normalizeReportePresupuestosPorUsuario,
  normalizeReportePresupuestosPorUsuarioDetalle,
  normalizeReporteStock,
  normalizeReportePrecios,
  normalizeReporteClientesDeudores,
  normalizeReporteCheques,
  normalizeReporteMovimientosCaja,
  normalizeReporteValorizacionStock,
  normalizeReporteRemitosAbiertos,
} from "./reportesNormalize";

import {
  emptyOnForbidden,
  isForbiddenError,
  isLogoutEndpoint,
  isServerError,
  shouldSkipSessionExpireRedirect,
} from "./httpErrors";
import { remitoEntregaBody } from "./remitoDeudorPassword";
import { cierreFinalizarBody } from "./cierreFinalizarPassword";
import { filenameFromContentDisposition, namedPdfFile } from "./openPdfTab";

export {
  emptyOnForbidden,
  isForbiddenError,
  isLogoutEndpoint,
  isServerError,
  shouldSkipSessionExpireRedirect,
};

// ============================================================================
// CONFIGURATION
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Helper to get media URLs with correct base (for avatar uploads, etc.)
export const getMediaUrl = (relativePath: string | null): string | null => {
  if (!relativePath) return null;
  // If already absolute, return as-is
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
    return relativePath;
  }
  // Use window.location.origin to get correct host:port
  return `${window.location.origin}${relativePath}`;
};

// ============================================================================
// TYPES
// ============================================================================

export interface APIError {
  error: string;
  message: string;
  /** DRF: `detail` string, objeto de campo, o cuerpo completo según respuesta. */
  details?: unknown;
  status?: number;
  afip_upstream?: boolean;
  /** Código de negocio si el backend lo envía (p.ej. config_hash_conflict). */
  code?: string;
  /** Cuerpo JSON completo de la respuesta de error (útil en 409). */
  data?: Record<string, unknown>;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Pagination request parameters
export interface PaginationParams {
  page?: number;
  page_size?: number;
}

// ============================================================================
// AUTHENTICATION HELPERS
// ============================================================================

export const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

function pdfFileFromResponse(response: Response, fallback: string): Promise<File> {
  return response.blob().then((blob) =>
    namedPdfFile(
      blob,
      filenameFromContentDisposition(response.headers.get("Content-Disposition"), fallback),
    ),
  );
}

export const getRefreshToken = (): string | null => {
  return localStorage.getItem('refreshToken');
};

/** Evita un segundo POST logout (interceptor 401 / doble click). Se resetea al loguear. */
let logoutInFlight: Promise<void> | null = null;
let sessionEnding = false;

/** Otras pestañas / broadcast: no redirigir ni reintentar logout por 401 en vuelo. */
export function beginSessionEnd(): void {
  sessionEnding = true;
}

export function isSessionEnding(): boolean {
  return sessionEnding;
}

export const setAuthTokens = (token: string, refreshToken: string): void => {
  sessionEnding = false;
  logoutInFlight = null;
  localStorage.setItem('authToken', token);
  localStorage.setItem('refreshToken', refreshToken);
};

export const clearAuthTokens = (): void => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
};

const mfaRememberKey = (username: string): string =>
  `mfaRemember:${String(username || "").trim().toLowerCase()}`;

export const getMfaRememberToken = (username: string): string => {
  if (!username) return "";
  try {
    return localStorage.getItem(mfaRememberKey(username)) || "";
  } catch {
    return "";
  }
};

export const setMfaRememberToken = (username: string, token: string): void => {
  if (!username || !token) return;
  try {
    localStorage.setItem(mfaRememberKey(username), token);
  } catch {
    /* ignore quota / private mode */
  }
};

function persistRememberFromPayload(data: Record<string, unknown>): void {
  const raw = String(data.rememberToken || data.remember_token || "");
  const user = data.user as { username?: string } | undefined;
  if (raw && user?.username) {
    setMfaRememberToken(user.username, raw);
  }
}

// Check if JWT token is expired
// Check if JWT token is expired
export const getTokenExpirationTime = (token: string | null): number | null => {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) {
      base64 += '='.repeat(4 - pad);
    }

    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    const decoded = JSON.parse(jsonPayload);
    const exp = decoded.exp;

    if (!exp) return null;
    return exp * 1000; // Return in milliseconds
  } catch (e) {
    console.error('Error parsing token expiration:', e);
    return null;
  }
};

export const isTokenExpired = (token: string | null): boolean => {
  const exp = getTokenExpirationTime(token);
  if (!exp) return true;
  return Date.now() >= exp;
};

// ============================================================================
// HTTP CLIENT
// ============================================================================

/** Valores admitidos en query string; se serializan con `String(value)` en `apiRequest`. */
export type QueryParamValue = string | number | boolean | null | undefined;

interface RequestOptions extends RequestInit {
  params?: Record<string, QueryParamValue>;
  /** No enviar Bearer; evita 401 de JWT inválido antes de permisos (p. ej. wizard ARCA durante needs_bootstrap). */
  omitAuth?: boolean;
}

function firstErrorString(value: unknown, depth = 0): string | null {
  if (depth > 6 || value == null) return null;
  if (typeof value === "string") {
    const text = value.trim();
    if (text && text !== "API Error" && text !== "Bad Request") return text;
    return null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstErrorString(item, depth + 1);
      if (found) return found;
    }
    return null;
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    for (const key of [
      "detail",
      "message",
      "codigo",
      "nombre",
      "sucursal",
      "nuevoPuntoVenta",
      "nuevo_punto_venta",
      "puntosVentaCodes",
      "puntos_venta_codes",
    ]) {
      if (key in obj) {
        const found = firstErrorString(obj[key], depth + 1);
        if (found) return found;
      }
    }
    for (const nested of Object.values(obj)) {
      const found = firstErrorString(nested, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { params, omitAuth, ...fetchOptions } = options;

  // Build URL with query parameters
  let url = `${API_BASE_URL}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  // Set default headers
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers,
  };

  // Add auth token if available (omit during anonymous first-run fiscal preview)
  if (!omitAuth) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // Handle non-JSON responses (like 204 No Content)
    if (response.status === 204) {
      return {} as T;
    }

    if (response.status === 401) {
      // Logout 401 = ya cerrado / token ausente. No reintentar, no loguear, no redirect.
      if (isLogoutEndpoint(endpoint)) {
        return {} as T;
      }
      // First-run setup usa /setup/* sin sesión; no redirigir (evita romper el wizard con token viejo).
      // Login 401 = credenciales / usuario inactivo: el formulario muestra el error; no recargar la página.
      if (sessionEnding) {
        throw {
          error: "Unauthorized",
          message: "Unauthorized",
          status: 401,
        } as APIError;
      }
      if (!shouldSkipSessionExpireRedirect(endpoint)) {
        console.warn("API 401 Unauthorized - redirecting to login");
        clearAuthTokens();
        localStorage.removeItem('currentUser');
        window.location.href = '/login';
        throw new Error("Unauthorized");
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // 403 = rol sin ese módulo (no sesión). No logout; no spamear consola en polls/catálogo.
      if (response.status !== 403) {
        console.error("API Request Failed:", {
          status: response.status,
          url: url,
          data: errorData
        });
      }
      // Try to extract useful details (DRF usa `detail` string u objeto)
      const details = errorData.details ?? errorData.detail ?? errorData;
      // Preferir mensaje de negocio; varios endpoints solo mandan `error` (texto largo) sin `message`.
      const errorField =
        typeof errorData.error === 'string' && errorData.error.trim() && errorData.error !== 'API Error'
          ? errorData.error
          : null;
      const detailsMessage = firstErrorString(details);
      const message =
        (typeof errorData.message === 'string' && errorData.message.trim() && errorData.message !== 'Bad Request'
          ? errorData.message
          : null) ||
        detailsMessage ||
        (typeof errorData.detail === 'string' ? errorData.detail : null) ||
        errorField ||
        response.statusText;
      throw {
        error: errorData.error || errorData.code || 'API Error',
        message,
        details,
        status: response.status,
        afip_upstream: errorData.afip_upstream,
        code: typeof errorData.code === 'string' ? errorData.code : undefined,
        data: errorData && typeof errorData === 'object' ? errorData : undefined,
      } as APIError;
    }

    const data = await response.json();

    return data as T;
  } catch (error) {
    // Re-throw API errors (misma condicion truthy que antes sobre `.error`)
    if (
      error !== null &&
      typeof error === "object" &&
      "error" in error &&
      Boolean((error as { error?: unknown }).error)
    ) {
      throw error;
    }

    // Handle network errors
    throw {
      error: 'Network Error',
      message: 'Unable to connect to the server',
      details: { originalError: error },
    } as APIError;
  }
}

// ============================================================================
// AUTHENTICATION API
// ============================================================================

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginUser {
  id: number;
  codigo: string;
  username: string;
  email: string;
  nombre: string;
  apellido: string;
  rol: string;
  isAdmin?: boolean;
  is_admin?: boolean;
  /** Django KEEP: full webapp. Login sends this, not is_staff. */
  isSuperuser?: boolean;
  is_superuser?: boolean;
  /** Derived: is_superuser OR rol.canonico. Never a lie of is_superuser. */
  isElevated?: boolean;
  is_elevated?: boolean;
  bloquearPersonalizadosExistentes?: boolean;
  bloquear_personalizados_existentes?: boolean;
  modulosPermitidos: number[];
  avatarUrl?: string;
  puntosVenta?: unknown[];
  puntoVentaDefault?: string | null;
  sessionDuration?: number;
  session_duration?: number;
}

export interface LoginResponse {
  user: LoginUser;
  token: string;
  refreshToken: string;
}

export type LoginOutcome =
  | { kind: "session"; user: LoginUser; token: string; refreshToken: string }
  | { kind: "mfa"; mfaToken: string }
  | { kind: "enroll"; enrollToken: string };

export interface MfaEnrollStartResponse {
  otpauthUri: string;
  qrPng: string;
  secret: string;
  issuer: string;
  account: string;
}

function persistFullSession(data: Record<string, unknown>): LoginOutcome & { kind: "session" } {
  persistRememberFromPayload(data);
  const token = String(data.access || data.token || "");
  const refreshToken = String(data.refresh || data.refreshToken || "");
  const user = data.user as LoginUser;
  if (token && refreshToken) {
    setAuthTokens(token, refreshToken);
  }
  if (user) {
    localStorage.setItem("currentUser", JSON.stringify(user));
  }
  return { kind: "session", user, token, refreshToken };
}

function sessionFromPayload(data: Record<string, unknown>): LoginOutcome & { kind: "session" } {
  return {
    kind: "session",
    user: data.user as LoginUser,
    token: String(data.access || data.token || ""),
    refreshToken: String(data.refresh || data.refreshToken || ""),
  };
}

export function persistAuthSession(session: {
  user: LoginUser;
  token: string;
  refreshToken: string;
}): void {
  if (session.token && session.refreshToken) {
    setAuthTokens(session.token, session.refreshToken);
  }
  if (session.user) {
    localStorage.setItem("currentUser", JSON.stringify(session.user));
  }
}

// ============================================================================
// OPCCIONES API (USUARIOS & ROLES)
// ============================================================================

export interface ModulePermission {
  moduleId: number;
  permission: 'none' | 'read' | 'full';
  sections?: Record<string, 'none' | 'read' | 'full'>;
  // Legado: se derivan de permission al guardar. El runtime usa read|full + sections.
  abmAccess?: boolean;
  listadosAccess?: boolean;
  actionColumn?: boolean;
}

export interface Rol {
  id?: number;
  codigo: string;
  nombre: string;
  descripcion: string;
  modulosPermitidos: ModulePermission[];
  sessionDuration?: number;
  activo: boolean;
  /** Backend: ROL001 / future root. Display name never locks. Not Caja/Vendedor. */
  isImmortal?: boolean;
  /** UI «Super Usuario». Not Django is_superuser. */
  canonico?: boolean;
  /** Ventas y presupuestos: no elegir un personalizado ya cargado. */
  bloquearPersonalizadosExistentes?: boolean;
}

export interface Usuario {
  id: number;
  codigo: string;
  username: string;
  nombre: string;
  apellido: string;
  email: string;
  telefono?: string;
  rol: string;
  isAdmin?: boolean;
  is_admin?: boolean;
  /** Django KEEP: full webapp. Read-only from login /me/; not is_staff. */
  isSuperuser?: boolean;
  is_superuser?: boolean;
  /** is_superuser OR rol.canonico. FE picker / hide; not a 403 substitute. */
  isElevated?: boolean;
  is_elevated?: boolean;
  /** Backend: seeded Administrator (USR001 / username admin). Not every staff user. */
  isImmortal?: boolean;
  is_immortal?: boolean;
  modulosPermitidos?: number[];
  avatar_url?: string;
  activo: boolean;
  isActive?: boolean;
  is_active?: boolean;
  /** True si hay movimientos, facturas, PV, etc. Eliminar desactiva en vez de borrar. */
  hasAssociatedData?: boolean;
  has_associated_data?: boolean;
  password?: string;
  puntosVenta?: { codigoPV: string; nombre: string; sucursal: string }[];
  puntoVentaDefault?: string | null;
  punto_venta_default?: string | null;
  mfaRequired?: boolean;
  mfa_required?: boolean;
  mfaEnrolled?: boolean;
  mfa_enrolled?: boolean;
}

export interface PuntoVenta {
  id: number;
  codigo: string;
  nombre: string;
  sucursal: number;
  sucursal_nombre?: string;
  sucursalNombre?: string;
  afipPuntoVenta?: number | null;
  afip_punto_venta?: number | null;
  activo: boolean;
}

export const puntosVentaAPI = {
  list: async (params?: { search?: string }): Promise<PuntoVenta[]> => {
    const response = await apiRequest<PaginatedResponse<PuntoVenta> | PuntoVenta[]>('/puntos-venta/', { params });
    return Array.isArray(response) ? response : response.results;
  },
};

export const usuariosAPI = {
  list: async (params?: { search?: string, rol?: string }): Promise<Usuario[]> => {
    const response = await apiRequest<PaginatedResponse<Usuario> | Usuario[]>('/opciones/usuarios/', { params });
    return Array.isArray(response) ? response : response.results;
  },

  get: async (codigo: string): Promise<Usuario> => {
    return apiRequest<Usuario>(`/opciones/usuarios/${encodeURIComponent(codigo)}/`);
  },

  create: async (data: Omit<Usuario, 'codigo'>): Promise<Usuario> => {
    return apiRequest<Usuario>('/opciones/usuarios/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (codigo: string, data: Partial<Usuario> & Record<string, unknown>): Promise<Usuario> => {
    return apiRequest<Usuario>(`/opciones/usuarios/${encodeURIComponent(codigo)}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  me: async (): Promise<Usuario> => {
    return apiRequest<Usuario>('/opciones/usuarios/me/');
  },

  verifyPassword: async (password: string): Promise<void> => {
    await apiRequest('/opciones/usuarios/me/verify-password/', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },

  updatePuntoVentaDefault: async (puntoVentaDefault: string | null): Promise<Usuario> => {
    return apiRequest<Usuario>('/opciones/usuarios/me/', {
      method: 'PATCH',
      body: JSON.stringify({ puntoVentaDefault }),
    });
  },

  delete: async (codigo: string): Promise<{ deactivated?: boolean; deleted?: boolean; message?: string }> => {
    return apiRequest<{ deactivated?: boolean; deleted?: boolean; message?: string }>(
      `/opciones/usuarios/${encodeURIComponent(codigo)}/`,
      {
        method: 'DELETE',
      },
    );
  },

  uploadAvatar: async (username: string, file: File): Promise<{ success: boolean; avatar_url: string | null }> => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('avatar', file);

    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE_URL}/opciones/usuarios/upload-avatar/`, {
      method: 'POST',
      headers,
      body: formData,
      // Don't set Content-Type - browser will set it with boundary for FormData
    });

    if (!response.ok) {
      const errBody = (await response.json().catch(() => ({}))) as {
        error?: string;
        detail?: string;
      };
      const msg =
        errBody.error ||
        (typeof errBody.detail === "string" ? errBody.detail : null) ||
        `Error al subir avatar (${response.status})`;
      throw new Error(msg);
    }

    return response.json();
  },

  getAvatar: async (username: string): Promise<{ avatar_url: string | null }> => {
    return apiRequest<{ avatar_url: string | null }>(`/opciones/usuarios/get-avatar/${username}/`);
  },

  resetMfa: async (codigo: string): Promise<{ ok?: boolean; mfaRequired?: boolean; username?: string }> => {
    return apiRequest(`/opciones/usuarios/${encodeURIComponent(codigo)}/mfa-reset/`, {
      method: "POST",
    });
  },

  disableMfa: async (codigo: string): Promise<{ ok?: boolean; mfaRequired?: boolean; username?: string }> => {
    return apiRequest(`/opciones/usuarios/${encodeURIComponent(codigo)}/mfa-disable/`, {
      method: "POST",
    });
  },
};

export const rolesAPI = {
  list: async (): Promise<Rol[]> => {
    const response = await apiRequest<PaginatedResponse<Rol> | Rol[]>('/opciones/roles/');
    return Array.isArray(response) ? response : response.results;
  },

  create: async (data: Partial<Rol>): Promise<Rol> => {
    return apiRequest<Rol>('/opciones/roles/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (codigo: string, data: Partial<Rol>): Promise<Rol> => {
    return apiRequest<Rol>(`/opciones/roles/${codigo}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete: async (codigo: string): Promise<void> => {
    return apiRequest<void>(`/opciones/roles/${codigo}/`, {
      method: 'DELETE',
    });
  },

  listUsuarios: async (codigo: string): Promise<Usuario[]> => {
    return apiRequest<Usuario[]>(`/opciones/roles/${codigo}/usuarios/`);
  },

  reassignUsers: async (
    codigo: string,
    usernames: string[],
  ): Promise<{ moved: Array<{ username: string; nombre: string; apellido: string; rolAnterior?: string | null }>; missing: string[]; rol: string }> => {
    return apiRequest(`/opciones/roles/${codigo}/reasignar-usuarios/`, {
      method: 'POST',
      body: JSON.stringify({ usernames }),
    });
  },

  removeUsers: async (
    codigo: string,
    usernames: string[],
  ): Promise<{ removed: string[] }> => {
    return apiRequest(`/opciones/roles/${codigo}/quitar-usuarios/`, {
      method: 'POST',
      body: JSON.stringify({ usernames }),
    });
  },
};

export const authAPI = {
  login: async (credentials: LoginRequest): Promise<LoginOutcome> => {
    const rememberToken = getMfaRememberToken(credentials.username);
    const data = await apiRequest<Record<string, unknown>>('/auth/login/', {
      method: 'POST',
      body: JSON.stringify(
        rememberToken ? { ...credentials, rememberToken } : credentials,
      ),
      omitAuth: true,
    });

    const enrollToken = String(data.enrollToken || data.enroll_token || "");
    const mfaToken = String(data.mfaToken || data.mfa_token || "");
    const access = data.access || data.token;
    if (data.mfaEnrollRequired && enrollToken && !access) {
      return { kind: "enroll", enrollToken };
    }
    if ((data.mfaRequired || data.mfa_required) && mfaToken && !access) {
      return { kind: "mfa", mfaToken };
    }
    return persistFullSession(data);
  },

  mfaEnrollStart: async (enrollToken: string): Promise<MfaEnrollStartResponse> => {
    const data = await apiRequest<Record<string, unknown>>("/auth/mfa/enroll/start/", {
      method: "POST",
      body: JSON.stringify({ enrollToken }),
      omitAuth: true,
    });
    return {
      otpauthUri: String(data.otpauthUri || data.otpauth_uri || ""),
      qrPng: String(data.qrPng || data.qr_png || ""),
      secret: String(data.secret || ""),
      issuer: String(data.issuer || "ABS"),
      account: String(data.account || ""),
    };
  },

  mfaEnrollConfirm: async (
    enrollToken: string,
    code: string,
    rememberDevice = false,
  ): Promise<LoginOutcome & { kind: "session"; backupCodes: string[] }> => {
    const data = await apiRequest<Record<string, unknown>>("/auth/mfa/enroll/confirm/", {
      method: "POST",
      body: JSON.stringify({ enrollToken, code, rememberDevice }),
      omitAuth: true,
    });
    persistRememberFromPayload(data);
    const session = sessionFromPayload(data);
    const raw = data.backupCodes || data.backup_codes;
    const backupCodes = Array.isArray(raw) ? raw.map((c) => String(c)) : [];
    return { ...session, backupCodes };
  },

  mfaVerify: async (
    mfaToken: string,
    code: string,
    rememberDevice = false,
  ): Promise<LoginOutcome & { kind: "session" }> => {
    const data = await apiRequest<Record<string, unknown>>("/auth/mfa/verify/", {
      method: "POST",
      body: JSON.stringify({ mfaToken, code, rememberDevice }),
      omitAuth: true,
    });
    return persistFullSession(data);
  },

  logout: async (): Promise<void> => {
    if (logoutInFlight) return logoutInFlight;
    beginSessionEnd();
    logoutInFlight = (async () => {
      try {
        await apiRequest<void>("/auth/logout/", { method: "POST" });
      } catch {
        // El cierre local sigue aunque el POST falle (token vencido, red).
      } finally {
        clearAuthTokens();
      }
    })();
    return logoutInFlight;
  },

  touchActivity: async (): Promise<void> => {
    await apiRequest("/auth/activity/", { method: "POST", body: JSON.stringify({}) });
  },

  refreshToken: async (): Promise<{ token: string; refreshToken: string }> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    const data = await apiRequest<any>('/auth/refresh/', {
      method: 'POST',
      body: JSON.stringify({ refresh: refreshToken }),
    });

    return {
      token: data.access || data.token,
      refreshToken: data.refresh || data.refreshToken || refreshToken
    };
  },

  getCurrentUser: async (_codigo?: string): Promise<any> => {
    return apiRequest<any>('/opciones/usuarios/me/');
  },
};

export interface ActivityLogEntry {
  id: number;
  createdAt: string;
  actorUsername: string;
  actorCodigo?: string;
  action: string;
  module: string;
  httpMethod: string;
  path: string;
  objectId?: string;
  summary?: string;
  success: boolean;
  payload?: Record<string, unknown>;
}

export interface ActivityLogListParams {
  page?: number;
  page_size?: number;
  usuario?: string;
  modulo?: string;
  fecha_desde?: string;
  fecha_hasta?: string;
}

function normalizeActivityLog(raw: Record<string, unknown>): ActivityLogEntry {
  const str = (a: unknown, b: unknown) =>
    String(a ?? b ?? "");
  return {
    id: Number(raw.id),
    createdAt: str(raw.createdAt, raw.created_at),
    actorUsername: str(raw.actorUsername, raw.actor_username),
    actorCodigo: str(raw.actorCodigo, raw.actor_codigo) || undefined,
    action: str(raw.action, ""),
    module: str(raw.module, ""),
    httpMethod: str(raw.httpMethod, raw.http_method),
    path: str(raw.path, ""),
    objectId: str(raw.objectId, raw.object_id) || undefined,
    summary: str(raw.summary, "") || undefined,
    success: Boolean(raw.success ?? true),
    payload:
      raw.payload && typeof raw.payload === "object"
        ? (raw.payload as Record<string, unknown>)
        : undefined,
  };
}

export const activityLogsAPI = {
  list: async (
    params: ActivityLogListParams = {},
  ): Promise<PaginatedResponse<ActivityLogEntry>> => {
    const response = await apiRequest<
      PaginatedResponse<ActivityLogEntry> | ActivityLogEntry[]
    >("/activity-logs/", { params });
    const resultsRaw = Array.isArray(response)
      ? response
      : (response.results ?? []);
    const results = resultsRaw.map((row) =>
      normalizeActivityLog(row as unknown as Record<string, unknown>),
    );
    if (Array.isArray(response)) {
      return { count: results.length, next: null, previous: null, results };
    }
    return {
      count: response.count ?? results.length,
      next: response.next ?? null,
      previous: response.previous ?? null,
      results,
    };
  },
};

// ============================================================================
// CLIENTES API
// ============================================================================

export interface Cliente {
  id?: number;
  codigo: string;
  nombre: string;
  cuit: string;
  dni?: string;
  domicilio?: string;
  direccion?: string; // Alias
  localidad?: string;
  provincia?: string;
  codigoPostal?: string;
  personaContacto?: string;
  telefono?: string;
  celular?: string;
  whatsapp?: string;
  email?: string;
  saldo: number;
  /** Un string en API (p. ej. A, B, A+X, B+X, X); el ABM lo decodifica a array. */
  tipoFacturaPermitida: string;
  condicionIva?: string;
  condicion_iva?: string; // Alias
  limiteCredito?: number;
  activo: boolean;
  observaciones?: string;
}

/** F5: nota de Estado de cuenta (pertenece al cliente). */
export interface ClienteNota {
  id: number;
  /** Título; notas viejas pueden venir vacías → UI “(Sin título)”. */
  titulo?: string;
  cuerpo: string;
  /** ISO con TZ. El renderer camelCase envía `createdAt`. */
  created_at?: string;
  createdAt?: string;
  /** `13/08/2026 17:32` (es-AR). CamelCase: `createdAtDisplay`. */
  created_at_display?: string;
  createdAtDisplay?: string;
  updated_at?: string;
  updatedAt?: string;
  created_by?: number | null;
  createdBy?: number | null;
  autor: string | null;
  autor_username?: string | null;
  autorUsername?: string | null;
}

function normalizeClienteNota(n: ClienteNota): ClienteNota {
  const created = n.created_at || n.createdAt || "";
  const display = n.created_at_display || n.createdAtDisplay || "";
  const titulo = typeof n.titulo === "string" ? n.titulo : "";
  return {
    ...n,
    titulo,
    created_at: created,
    createdAt: created,
    created_at_display: display,
    createdAtDisplay: display,
  };
}

export interface ClienteMovimiento {
  id?: number;
  clienteId: number;
  tipo: string;
  referencia: string;
  fecha: string;
  descripcion: string;
  debe: number;
  haber: number;
  saldo: number;
  facturaId?: number;
  confirmationType?: 'cae' | 'fx';
  tipoComprobante?: string;
  isNC?: boolean;
}

export const clientesAPI = {
  list: async (params?: { search?: string; activo?: boolean; page_size?: number }): Promise<Cliente[]> => {
    const response = await apiRequest<PaginatedResponse<Cliente> | Cliente[]>('/clientes/', { params });
    return Array.isArray(response) ? response : response.results;
  },

  listPaginated: async (params?: { search?: string; letter?: string; activo?: boolean } & PaginationParams): Promise<PaginatedResponse<Cliente>> => {
    return apiRequest<PaginatedResponse<Cliente>>('/clientes/', { params });
  },

  /** Iniciales A–Z que tienen al menos un cliente. */
  letras: async (): Promise<string[]> => {
    return apiRequest<string[]>('/clientes/letras/');
  },

  /**
   * Búsqueda contextual (capa 1). No es el listado ABM de Clientes.
   * GET /api/clientes/lookup/?search= | ?codigo=
   */
  lookup: async (params?: {
    search?: string;
    codigo?: string;
    page_size?: number;
  }): Promise<Cliente[]> => {
    const response = await apiRequest<PaginatedResponse<Cliente> | Cliente[]>(
      '/clientes/lookup/',
      { params },
    );
    return Array.isArray(response) ? response : (response.results ?? []);
  },

  get: async (codigo: string): Promise<Cliente> => {
    return apiRequest<Cliente>(`/clientes/${codigo}/`);
  },

  create: async (data: Omit<Cliente, 'id' | 'codigo' | 'saldo'>): Promise<Cliente> => {
    return apiRequest<Cliente>('/clientes/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (codigo: string, data: Partial<Cliente>): Promise<Cliente> => {
    return apiRequest<Cliente>(`/clientes/${codigo}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete: async (codigo: string): Promise<void> => {
    return apiRequest<void>(`/clientes/${codigo}/`, {
      method: 'DELETE',
    });
  },

  getNextCodigo: async (): Promise<{ codigo: string }> => {
    return apiRequest<{ codigo: string }>('/clientes/next-codigo/');
  },

  /**
   * Check unicidad DNI/CUIT entre clientes vivos.
   * GET /api/clientes/check-documento/?tipo=cuit|dni&valor=...&exclude_codigo=
   */
  checkDocumento: async (params: {
    tipo: 'cuit' | 'dni';
    valor: string;
    excludeCodigo?: string | null;
  }): Promise<{
    ok: boolean;
    available: boolean;
    tipo: 'cuit' | 'dni';
    valor: string | null;
    conflict: { codigo: string; nombre: string } | null;
    message?: string;
  }> => {
    return apiRequest('/clientes/check-documento/', {
      params: {
        tipo: params.tipo,
        valor: params.valor,
        exclude_codigo: params.excludeCodigo || undefined,
      },
    });
  },

  getMovimientos: async (codigo: string, params?: { fechaDesde?: string; fechaHasta?: string }): Promise<ClienteMovimiento[]> => {
    const response = await apiRequest<PaginatedResponse<ClienteMovimiento> & {
      saldoActual?: number;
      saldo_actual?: number;
    }>(`/clientes/${codigo}/movimientos/`, { params });
    return response.results;
  },

  getEstadoCuenta: async (codigo: string): Promise<{
    movimientos: ClienteMovimiento[];
    saldoActual: number;
  }> => {
    const response = await apiRequest<PaginatedResponse<ClienteMovimiento> & {
      saldoActual?: number;
      saldo_actual?: number;
    }>(`/clientes/${codigo}/movimientos/`);
    const saldoRaw = response.saldoActual ?? response.saldo_actual;
    return {
      movimientos: response.results || [],
      saldoActual: saldoRaw == null || saldoRaw === "" ? NaN : Number(saldoRaw),
    };
  },

  listNotas: async (codigo: string): Promise<ClienteNota[]> => {
    const response = await apiRequest<PaginatedResponse<ClienteNota> | ClienteNota[]>(
      `/clientes/${codigo}/notas/`
    );
    const rows = Array.isArray(response) ? response : response.results;
    return rows.map(normalizeClienteNota);
  },

  createNota: async (
    codigo: string,
    data: { titulo: string; cuerpo: string }
  ): Promise<ClienteNota> => {
    const nota = await apiRequest<ClienteNota>(`/clientes/${codigo}/notas/`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return normalizeClienteNota(nota);
  },

  updateNota: async (
    codigo: string,
    notaId: number,
    data: { titulo: string; cuerpo: string }
  ): Promise<ClienteNota> => {
    const nota = await apiRequest<ClienteNota>(`/clientes/${codigo}/notas/${notaId}/`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return normalizeClienteNota(nota);
  },

  deleteNota: async (codigo: string, notaId: number): Promise<void> => {
    return apiRequest<void>(`/clientes/${codigo}/notas/${notaId}/`, {
      method: "DELETE",
    });
  },

  /**
   * Get all facturas for a specific cliente
   * TODO: [BACKEND] Implement this endpoint
   * 
   * BACKEND REQUIREMENTS:
   * 1. Endpoint: GET /api/clientes/{codigo}/facturas/
   * 2. Authentication: Required (Bearer token)
   * 3. Authorization: User must have permission to view this cliente's data
   * 
   * 4. Query Parameters:
   *    - fechaDesde (optional): ISO date string for filtering from date
   *    - fechaHasta (optional): ISO date string for filtering to date
   *    - estado (optional): Filter by estado (Pendiente, Confirmado - CAE/AFIP, Confirmado - Negro)
   *    - limit (optional): Pagination limit
   *    - offset (optional): Pagination offset
   * 
   * 5. Response Structure:
   * {
   *   count: number,
   *   next: string | null,
   *   previous: string | null,
   *   results: [
   *     {
   *       id: number,
   *       numero: string,
   *       fecha: string (ISO format),
   *       cliente: {
   *         id: number,
   *         codigo: string,
   *         nombre: string,
   *         cuit: string,
   *         domicilio: string,
   *         condicionIVA: string
   *       },
   *       total: number,
   *       estado: string,
   *       cae: string | null,
   *       metodosPago: [
   *         { metodoPago: string, importe: number }
   *       ],
   *       items: [
   *         {
   *           codigo: string,
   *           descripcion: string,
   *           cantidad: number,
   *           precioUnitario: number,
   *           total: number
   *         }
   *       ],
   *       pdfUrl: string | null
   *     }
   *   ]
   * }
   * 
   * 6. Database Query Optimization:
   *    - Use select_related() for cliente, punto_venta
   *    - Use prefetch_related() for factura_detalle
   *    - Filter by cliente.codigo = codigo parameter
   *    - Order by fecha DESC
   * 
   * 7. Security Considerations:
   *    - Verify user has permission to view this cliente
   *    - Only return facturas for the specified cliente
   *    - Don't expose sensitive pricing if user doesn't have permission
   */
  getFacturas: async (_codigo: string, _params?: {
    fechaDesde?: string;
    fechaHasta?: string;
    estado?: string;
  }): Promise<any[]> => {
    // TODO: Uncomment when backend is ready
    // return apiRequest<any[]>(`/clientes/${codigo}/facturas/`, { params });
    throw new Error('API not implemented yet');
  },
};

// ============================================================================
// PROVEEDORES API
// ============================================================================

export interface Proveedor {
  id?: number;
  codigo: string;
  nombreRazonSocial: string;
  cuit: string;
  domicilio?: string;
  localidad?: string;
  provincia?: string;
  codigoPostal?: string;
  personaContacto?: string;
  telefono?: string;
  celular?: string;
  whatsapp?: string;
  email?: string;
  saldo: number;
  condicionPago?: string;
  activo: boolean;
  observaciones?: string;
}

export interface ProveedorPedidoProducto {
  codigo: string;
  nombre: string;
  cantidad: number;
  precioBase: number;
  /** % (0–100), opcional; backend puede devolver precio_base / descuento_1 según serialización. */
  descuento1?: number;
  descuento2?: number;
  precio_base?: number | string;
  descuento_1?: number;
  descuento_2?: number;
  recibido: boolean;
  /** Agrupa las dos patas. La grilla muestra una línea; el pedido guarda dos ítems. */
  comboGroup?: string | null;
  combo_group?: string | null;
  /** Solo el PDF. No cambia el nombre del artículo. */
  nombrePdf?: string | null;
  nombre_pdf?: string | null;
  comboNombrePdf?: string | null;
  combo_nombre_pdf?: string | null;
}

export interface ProveedorPedido {
  id?: number;
  codigo: string;
  proveedorId: number;
  nroPedido: string;
  facturaNro?: string;
  pdfRemitoUrl?: string;
  estado: 'pendiente' | 'parcial' | 'completo' | 'cancelado' | 'recibido';
  fechaCreacion: string;
  /** % de cabecera sobre la suma de líneas. Editar el importe lo recalcula. */
  descuentoTotal?: number;
  fechaFinalizado?: string;
  importe?: number;
  productos?: ProveedorPedidoProducto[];
  observaciones?: string;
}

export interface ProveedorPago {
  id?: number;
  codigo?: string;
  numeroPago: string;
  proveedor?: number; // Optional - backend derives from proveedor_codigo
  proveedorId?: number; // Alias for backwards compat
  proveedorNombre?: string;
  pedido?: number;
  pedidoId?: number; // Alias for backwards compat
  pedidoNumero?: string;
  nroPedido?: string; // Alias for backwards compat
  remito?: number;
  remitoNumero?: string;
  fechaPago: string;
  fecha?: string; // Alias for backwards compat
  metodosPago: Array<{ tipo: string; monto: number; chequeId?: string; chequeNumero?: string; chequeBanco?: string; chequeTipo?: string; chequeFechaCreacion?: string; chequeFecha?: string }>;
  totalPago: number;
  /** Snapshot / saldo post-pago; solo respuesta; el POST no debe enviarlos (E.2). */
  deudaProveedor?: number;
  saldo?: number;
  pdfFacturaUrl?: string;
  pdfFacturaName?: string;
  facturaPdfUrl?: string; // Alias for backwards compat
  facturaPdfName?: string; // Alias for backwards compat
  /** Talonario REC-PROV del PDF recibo (no PAG- interno ni REC de clientes). */
  numeroComprobante?: string | null;
  pdfUrl?: string | null;
  notas?: string;
}

export interface ProveedorSaldoHistoryRow {
  id: number;
  proveedor: number;
  saldoAnterior: number | string;
  saldoNuevo: number | string;
  montoCambio: number | string;
  tipoMovimiento: string;
  referenciaId?: string | null;
  descripcion?: string | null;
  createdAt: string;
  createdBy?: string | null;
}

export interface ProveedorRecibo {
  id?: number;
  proveedorId: number;
  pedidoId?: number;
  fecha: string;
  metodoPago: string;
  importe: number;
  observaciones?: string;
}

export interface ProveedorRemitoPdfSnapshot {
  url: string;
  estado: string;
  at?: string;
}

export interface ProveedorRemito {
  id?: number;
  codigo: string;
  proveedorId: number;
  proveedorCodigo?: string; // Added for backwards compat
  nroRemito?: string;
  numeroRemito?: string; // Alias for nroRemito
  pdfRemitoUrl?: string;
  pedidoId?: number;
  pedido?: number; // Alias
  pedidoNumero?: string; // From serializer
  nroPedido?: string; // Alias for backwards compat
  fechaCreacion: string;
  fechaFinalizado?: string;
  importe?: number;
  montoPagado?: number; // For tracking partial payments
  productos?: ProveedorPedidoProducto[];
  observaciones?: string;
  estado?: 'pendiente' | 'parcial' | 'recibido' | 'parcial_pago' | 'pagado' | 'finalizado';
  pdfSnapshots?: ProveedorRemitoPdfSnapshot[];
  pdf_snapshots?: ProveedorRemitoPdfSnapshot[];
  pdfUrl?: string | null;
  pdf_url?: string | null;
  usuarioNombre?: string | null;
  usuario_nombre?: string | null;
  createdBy?: number | null;
  created_by?: number | null;
  finalizadoUsuarioNombre?: string | null;
  finalizado_usuario_nombre?: string | null;
}

export const proveedoresAPI = {
  list: async (params?: { search?: string; activo?: boolean }): Promise<Proveedor[]> => {
    const out: Proveedor[] = [];
    let page = 1;
    const page_size = 200;
    for (;;) {
      const response = await apiRequest<PaginatedResponse<Proveedor>>('/proveedores/', {
        params: { ...params, page, page_size },
      });
      const batch = response.results ?? [];
      out.push(...batch);
      if (!response.next || batch.length === 0) break;
      page += 1;
      if (page > 200) break;
    }
    return out;
  },

  /**
   * Lookup contextual (capa 1) para el combo Otras fábricas.
   * GET /api/proveedores/lookup/ — no exige módulo Proveedores.
   */
  lookup: async (params?: {
    search?: string;
    activo?: boolean;
    page_size?: number;
  }): Promise<Proveedor[]> => {
    const response = await apiRequest<PaginatedResponse<Proveedor> | Proveedor[]>(
      '/proveedores/lookup/',
      { params },
    );
    return Array.isArray(response) ? response : (response.results ?? []);
  },

  listPaginated: async (params?: { search?: string; activo?: boolean } & PaginationParams): Promise<PaginatedResponse<Proveedor>> => {
    return apiRequest<PaginatedResponse<Proveedor>>('/proveedores/', { params });
  },

  get: async (codigo: string): Promise<Proveedor> => {
    return apiRequest<Proveedor>(`/proveedores/${codigo}/`);
  },

  create: async (data: Omit<Proveedor, 'id' | 'codigo' | 'saldo'>): Promise<Proveedor> => {
    // Strip fields that shouldn't be in create payload
    const { codigo: _codigo, saldo, id, ...payload } = data as any;
    return apiRequest<Proveedor>('/proveedores/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  update: async (codigo: string, data: Partial<Proveedor>): Promise<Proveedor> => {
    return apiRequest<Proveedor>(`/proveedores/${codigo}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete: async (codigo: string): Promise<void> => {
    return apiRequest<void>(`/proveedores/${codigo}/`, {
      method: 'DELETE',
    });
  },

  getNextCodigo: async (): Promise<{ codigo: string }> => {
    return apiRequest<{ codigo: string }>('/proveedores/next-codigo/');
  },

  // Pedidos
  getPedidos: async (codigo?: string, params?: { historico?: 0 | 1 }): Promise<ProveedorPedido[]> => {
    const response = await apiRequest<PaginatedResponse<ProveedorPedido> | ProveedorPedido[]>('/proveedores-pedidos/', {
      params: { proveedor__codigo: codigo, ...params },
    });
    // console.log('getPedidos API response:', response);
    if (Array.isArray(response)) return response;
    return response.results || [];
  },

  /** Histórico de pedidos (recibidos): paginado en el backend, filtro por fecha del pedido. */
  getPedidosHistorico: async (params: {
    historico: 1;
    fecha_desde?: string;
    fecha_hasta?: string;
    proveedor__codigo?: string;
    page: number;
    page_size: number;
  }): Promise<PaginatedResponse<ProveedorPedido>> => {
    return apiRequest<PaginatedResponse<ProveedorPedido>>('/proveedores-pedidos/', { params });
  },

  /** Historial de saldo (pedidos = debe, pagos = haber), más nuevo primero. */
  getSaldoHistory: async (codigo: string): Promise<ProveedorSaldoHistoryRow[]> => {
    const out: ProveedorSaldoHistoryRow[] = [];
    let page = 1;
    for (;;) {
      const response = await apiRequest<PaginatedResponse<ProveedorSaldoHistoryRow>>(
        '/proveedores-saldo-history/',
        { params: { proveedor__codigo: codigo, page, page_size: 200 } },
      );
      const batch = response.results ?? [];
      out.push(...batch);
      if (!response.next || batch.length === 0) break;
      page += 1;
      if (page > 200) break;
    }
    return out;
  },

  createPedido: async (codigo: string, data: Omit<ProveedorPedido, 'id' | 'codigo' | 'proveedorId'>): Promise<ProveedorPedido> => {
    // We need the proveedor ID, but the API might accept codigo or we need to look it up
    // The backend ViewSet for ProveedorPedido is likely standard.
    // WARNING: The backend likely expects `proveedor` ID in the body or standard relationship.
    // Let's assume standard DRF ViewSet for now.
    // But wait, the URL structure in mock was `/proveedores/${codigo}/pedidos/`
    // In backend `urls.py` we have `router.register(r'proveedores-pedidos', ProveedorPedidoViewSet)`
    // So the endpoint is `/proveedores-pedidos/`.
    // And it likely expects `proveedor` field in body.
    // I should probably fetch the proveedor first to get ID? Or backend allows nesting?
    // For now, I will use `/proveedores-pedidos/` and assume data contains necessary links or I need to fix it.
    // Actually, looking at the `mock` implementation: `createPedido` takes `codigo` (of proveedor).
    // I should probably query by codigo if needed. 
    // Let's use the `/proveedores-pedidos/` endpoint.
    return apiRequest<ProveedorPedido>('/proveedores-pedidos/', {
      method: 'POST',
      body: JSON.stringify({ ...data, proveedor_codigo: codigo }), // Provide codigo if backend serializer supports it, or I need to handle this.
    });
  },

  updatePedido: async (_codigo: string, pedidoId: number, data: Partial<ProveedorPedido>): Promise<ProveedorPedido> => {
    return apiRequest<ProveedorPedido>(`/proveedores-pedidos/${pedidoId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  updatePedidoItemNombre: async (
    pedidoId: number,
    data: { codigo: string; comboGroup?: string | null; nombreProducto: string },
  ): Promise<ProveedorPedido> => {
    return apiRequest<ProveedorPedido>(`/proveedores-pedidos/${pedidoId}/update-item-nombre/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deletePedido: async (pedidoId: number): Promise<void> => {
    await apiRequest<void>(`/proveedores-pedidos/${pedidoId}/`, {
      method: 'DELETE',
    });
  },

  downloadPedidoPdf: async (pedidoId: number): Promise<File> => {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE_URL}/proveedores-pedidos/${pedidoId}/pdf/`, {
      method: 'GET',
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw {
        error: errBody.error || 'pdf_error',
        message: errBody.message || `PDF pedido proveedor falló: ${response.status}`,
        details: errBody,
        status: response.status,
      } as APIError;
    }
    return pdfFileFromResponse(response, "rpp_comprobante.pdf");
  },

  // Pagos
  getPagos: async (codigo?: string): Promise<ProveedorPago[]> => {
    const query = codigo ? `?proveedor__codigo=${codigo}` : '';
    const response = await apiRequest<PaginatedResponse<ProveedorPago>>(`/proveedores-pagos/${query}`);
    return response.results;
  },

  createPago: async (
    codigo: string,
    data: Omit<ProveedorPago, 'id' | 'proveedorId' | 'saldo' | 'deudaProveedor'>,
  ): Promise<ProveedorPago> => {
    return apiRequest<ProveedorPago>('/proveedores-pagos/', {
      method: 'POST',
      body: JSON.stringify({ ...data, proveedor_codigo: codigo }),
    });
  },

  fechasPagoDisponibles: async (): Promise<{ fechas: string[]; default?: string | null }> => {
    return apiRequest<{ fechas: string[]; default?: string | null }>(
      '/proveedores-pagos/fechas-disponibles/',
    );
  },

  downloadPagoPdf: async (pagoId: number): Promise<File> => {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE_URL}/proveedores-pagos/${pagoId}/pdf/`, {
      method: 'GET',
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw {
        error: errBody.error || 'pdf_error',
        message: errBody.message || `PDF recibo de pago falló: ${response.status}`,
        details: errBody,
        status: response.status,
      } as APIError;
    }
    return pdfFileFromResponse(response, "rcp_comprobante.pdf");
  },

  // Recibos - This might not be implemented in backend yet or named differently?
  // Backend has `ProveedorSaldoHistory`? 
  // Let's check backend urls.py again. `ProveedorPagoViewSet` exists. `ProveedorRemitoViewSet` exists.
  // There is no `Recibo` viewset explicitly for Proveedores (only Pagos).
  // Maybe Recibos correspond to Pagos in the backend model?
  // Frontend interface `ProveedorRecibo` vs `ProveedorPago`.
  // I'll comment out Recibos for now or map to Pagos if they are same.

  getRecibos: async (_codigo: string): Promise<ProveedorRecibo[]> => {
    // Assuming Recibos are modeled as Pagos or similar
    // return apiRequest<ProveedorRecibo[]>(`/proveedores-pagos/?proveedor__codigo=${codigo}&tipo=recibo`);
    throw new Error('Recibos API not mapped to Backend yet');
  },

  createRecibo: async (_codigo: string, _data: Omit<ProveedorRecibo, 'id' | 'proveedorId'>): Promise<ProveedorRecibo> => {
    throw new Error('Recibos API not mapped to Backend yet');
  },

  // Remitos
  getRemitos: async (params?: { proveedor__codigo?: string; pedido__numero_pedido?: string }): Promise<ProveedorRemito[]> => {
    const response = await apiRequest<PaginatedResponse<ProveedorRemito> | ProveedorRemito[]>('/proveedores-remitos/', { params });
    // console.log('getRemitos API response:', response);

    if (Array.isArray(response)) return response;

    // Strict check for results array
    if (response && Array.isArray(response.results)) {
      return response.results;
    }

    // Default fallback
    console.warn('getRemitos: Invalid response format, defaulting to []', response);
    return [];
  },

  downloadProveedorRemitoPdf: async (remitoId: number, version: string = 'latest'): Promise<File> => {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const qs = version ? `?version=${encodeURIComponent(version)}` : '';
    const response = await fetch(`${API_BASE_URL}/proveedores-remitos/${remitoId}/pdf/${qs}`, {
      method: 'GET',
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw {
        error: errBody.error || 'pdf_error',
        message: errBody.message || `PDF remito proveedor falló: ${response.status}`,
        details: errBody,
        status: response.status,
      } as APIError;
    }
    return pdfFileFromResponse(response, "rp_comprobante.pdf");
  },

  createRemito: async (codigo: string, data: Omit<ProveedorRemito, 'id' | 'codigo' | 'proveedorId'>): Promise<ProveedorRemito> => {
    return apiRequest<ProveedorRemito>('/proveedores-remitos/', {
      method: 'POST',
      body: JSON.stringify({ ...data, proveedor_codigo: codigo }),
    });
  },

  updateRemito: async (
    remitoId: string | number,
    data: Partial<ProveedorRemito> & {
      sucursalId?: number;
      depositoId?: number;
      sucursal_id?: number;
      deposito_id?: number;
    },
  ): Promise<ProveedorRemito> => {
    return apiRequest<ProveedorRemito>(`/proveedores-remitos/${remitoId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

// ============================================================================
// PEDIDOS REPOSICIÓN (F6)
// ============================================================================

export interface PedidoReposicionItemRow {
  productoId: number;
  productoCodigo: string;
  productoNombre: string;
  cantidadVendida: string | number;
  cantidadSugerida: number;
  estado: "pendiente" | "generado" | "omitido";
  pedidoProveedorId: number | null;
  nroPedido: string | null;
  reposicionItemId?: number | null;
  precioBase: string | number;
  /** Suma de depósitos (Stock Total). */
  stockActual?: number | string;
  /** Qty en remitos pendiente/parcial no entregada. */
  stockReservado?: number | string;
  facturaDetalleIds: number[];
}

export interface PedidoReposicionGrupo {
  proveedorId: number | null;
  proveedorCodigo: string | null;
  proveedorNombre: string;
  items: PedidoReposicionItemRow[];
}

export interface PedidoReposicionListado {
  fechaDesde: string;
  fechaHasta: string;
  vista?: "pendiente" | "historico" | "todos";
  grupos: PedidoReposicionGrupo[];
}

export interface PedidoProductoVentaRemito {
  numero: string;
  estado: string;
  clienteId?: number | null;
}

export interface PedidoProductoVenta {
  vendedor: string;
  cantidad: string | number;
  facturaTipo: string;
  facturaNumero: string;
  facturaId: number;
  fecha: string;
  remitos: PedidoProductoVentaRemito[];
}

export interface PedidoProductoVentas {
  filas: PedidoProductoVenta[];
  hayMas?: boolean;
}

export const pedidosReposicionAPI = {
  list: async (params: {
    fechaDesde: string;
    fechaHasta: string;
    vista?: "pendiente" | "historico" | "todos";
    q?: string;
  }): Promise<PedidoReposicionListado> => {
    return apiRequest<PedidoReposicionListado>("/pedidos-reposicion/", {
      params: {
        fecha_desde: params.fechaDesde,
        fecha_hasta: params.fechaHasta,
        ...(params.vista ? { vista: params.vista } : {}),
        ...(params.q ? { q: params.q } : {}),
      },
    });
  },

  generar: async (data: {
    proveedorId: number;
    fechaDesde: string;
    fechaHasta: string;
    items: { productoId: number; cantidad: number }[];
  }): Promise<ProveedorPedido> => {
    return apiRequest<ProveedorPedido>("/pedidos-reposicion/generar/", {
      method: "POST",
      body: JSON.stringify({
        proveedorId: data.proveedorId,
        fechaDesde: data.fechaDesde,
        fechaHasta: data.fechaHasta,
        items: data.items.map((i) => ({ productoId: i.productoId, cantidad: i.cantidad })),
      }),
    });
  },

  omitir: async (data: {
    productoId: number;
    fechaDesde: string;
    fechaHasta: string;
  }): Promise<{ id: number; productoId: number; estado: string }> => {
    return apiRequest("/pedidos-reposicion/omitir/", {
      method: "POST",
      body: JSON.stringify({
        productoId: data.productoId,
        fechaDesde: data.fechaDesde,
        fechaHasta: data.fechaHasta,
      }),
    });
  },

  restaurar: async (data: {
    productoId: number;
    fechaDesde: string;
    fechaHasta: string;
  }): Promise<{ ok: boolean; productoId: number }> => {
    return apiRequest("/pedidos-reposicion/restaurar/", {
      method: "POST",
      body: JSON.stringify({
        productoId: data.productoId,
        fechaDesde: data.fechaDesde,
        fechaHasta: data.fechaHasta,
      }),
    });
  },

  productoVentas: async (params: {
    productoId: number;
    fechaDesde: string;
    fechaHasta: string;
  }): Promise<PedidoProductoVentas> => {
    return apiRequest<PedidoProductoVentas>("/pedidos-reposicion/producto-ventas/", {
      params: {
        producto_id: params.productoId,
        fecha_desde: params.fechaDesde,
        fecha_hasta: params.fechaHasta,
      },
    });
  },
};

// ============================================================================
// PRODUCTOS API
// ============================================================================

export interface ProductoGrupo {
  id?: number;
  codigo: string;
  nombre: string;
  descripcion?: string;
  activo: boolean;
}

export interface ProductoSubgrupo {
  id?: number;
  codigo: string;
  nombre: string;
  /** FK ProductoGrupo (respuesta API camelCase; el campo DRF es `grupo`) */
  grupo?: number;
  grupoId?: number;
  grupoNombre?: string;
  descripcion?: string;
  activo: boolean;
}

/** Ubicación de stock en listado de productos (depósito + sucursales asociadas). */
export interface ProductoUbicacion {
  sucursal?: number | null;
  sucursalNombre?: string;
  sucursal_nombre?: string;
  sucursales?: number[];
  sucursalesNombres?: string[];
  sucursales_nombres?: string[];
  deposito?: number;
  depositoNombre?: string;
  deposito_nombre?: string;
  cantidad: number | string;
}

export interface Producto {
  id?: number;
  codigo: string;
  nombre: string;
  descripcion?: string;
  /** FK en API DRF (camelCase): id del ProductoGrupo */
  grupo?: number | null;
  grupoId?: number;
  grupo_nombre?: string;
  grupoNombre?: string;
  /** FK: id del ProductoSubgrupo */
  subgrupo?: number | null;
  subgrupoId?: number;
  subgrupo_nombre?: string;
  subgrupoNombre?: string;
  proveedorNombre?: string;
  proveedor_nombre?: string;
  codigoBarras?: string;
  codigoProveedor?: string;
  codigo_proveedor?: string;
  unidadMedida: string;
  precioBase: number;
  ivaPorcentaje: number;
  gananciaPorcentaje: number;
  precioFinal: number;
  precio_final?: number; // Alias for backwards compat
  precio_base?: number | string;
  stockActual?: number;
  stock_actual?: number; // Alias for backwards compat
  /** Qty en remitos pendiente/parcial no entregada (columna Stock Total). */
  stockReservado?: number;
  stock_reservado?: number;
  /** Cantidad en pedidos a proveedor todavía no recibidos. */
  stockPedido?: number;
  stock_pedido?: number;
  /** Depósito − remitos pendiente/parcial (informativo; no bloquea venta). */
  stockDisponible?: number;
  stock_disponible?: number;
  stockControlado?: boolean;
  stock_controlado?: boolean;
  stockMinimo?: number;
  stock_minimo?: number; // Alias for backwards compat
  /** Cantidad de filas Stock (depósitos) del producto. Anotado en listado. */
  ubicacionesCount?: number;
  ubicaciones_count?: number;
  /** Ubicaciones con qty ≠ 0 (listado Stock sin abrir ABM). */
  ubicaciones?: ProductoUbicacion[];
  activo: boolean;
  observaciones?: string;
  proveedor?: number | string | any;
  /** H.A.5 — producto creado por configurador */
  esPersonalizado?: boolean;
  origenConfigurador?: 'FAVRE' | 'MANUAL' | null;
  configHash?: string | null;
}

export const productosAPI = {
  // Grupos (router Django: /rubros/, no /productos/grupos/)
  listGrupos: async (opts?: { includeInactive?: boolean }): Promise<ProductoGrupo[]> => {
    const params: Record<string, string | number> = { page_size: 2000 };
    if (opts?.includeInactive) params.include_inactive = 'true';
    const response = await apiRequest<PaginatedResponse<ProductoGrupo> | ProductoGrupo[]>('/rubros/', {
      params,
    });
    return Array.isArray(response) ? response : response.results ?? [];
  },

  createGrupo: async (data: {
    nombre: string;
    descripcion?: string;
    activo?: boolean;
    /** Si se omite o va vacío, el backend asigna código único a partir del nombre. */
    codigo?: string;
  }): Promise<ProductoGrupo> => {
    const body: Record<string, unknown> = {
      nombre: data.nombre,
      descripcion: data.descripcion ?? '',
      activo: data.activo ?? true,
    };
    if (data.codigo != null && String(data.codigo).trim() !== '') {
      body.codigo = String(data.codigo).trim();
    }
    return apiRequest<ProductoGrupo>('/rubros/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  updateGrupo: async (
    codigo: string,
    data: Partial<Pick<ProductoGrupo, 'nombre' | 'descripcion' | 'activo'>>
  ): Promise<ProductoGrupo> => {
    return apiRequest<ProductoGrupo>(`/rubros/${encodeURIComponent(codigo)}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteGrupo: async (codigo: string): Promise<void> => {
    await apiRequest<void>(`/rubros/${encodeURIComponent(codigo)}/`, {
      method: 'DELETE',
    });
  },

  // Subgrupos (/subrubros/; filtro ?grupo=<id> del FK)
  listSubgrupos: async (
    grupoId?: number,
    opts?: { includeInactive?: boolean }
  ): Promise<ProductoSubgrupo[]> => {
    const params: Record<string, string | number> = { page_size: 2000 };
    if (grupoId) params.grupo = grupoId;
    if (opts?.includeInactive) params.include_inactive = 'true';
    const response = await apiRequest<PaginatedResponse<ProductoSubgrupo> | ProductoSubgrupo[]>('/subrubros/', {
      params,
    });
    return Array.isArray(response) ? response : response.results ?? [];
  },

  createSubgrupo: async (data: {
    nombre: string;
    grupo: number;
    descripcion?: string | null;
    activo?: boolean;
    codigo?: string;
  }): Promise<ProductoSubgrupo> => {
    const body: Record<string, unknown> = {
      nombre: data.nombre,
      descripcion: data.descripcion ?? '',
      activo: data.activo ?? true,
      grupo: data.grupo,
    };
    if (data.codigo != null && String(data.codigo).trim() !== '') {
      body.codigo = String(data.codigo).trim();
    }
    return apiRequest<ProductoSubgrupo>('/subrubros/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  updateSubgrupo: async (
    codigo: string,
    data: Partial<Pick<ProductoSubgrupo, 'nombre' | 'descripcion' | 'activo'>> & { grupo?: number }
  ): Promise<ProductoSubgrupo> => {
    return apiRequest<ProductoSubgrupo>(`/subrubros/${encodeURIComponent(codigo)}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteSubgrupo: async (codigo: string): Promise<void> => {
    await apiRequest<void>(`/subrubros/${encodeURIComponent(codigo)}/`, {
      method: 'DELETE',
    });
  },

  // Productos
  list: async (params?: {
    search?: string;
    grupoId?: number;
    subgrupoId?: number;
    /** H.A.7 — filtrar productos del configurador */
    esPersonalizado?: boolean;
    /** Pedido OC: Producto.proveedor_id (mismo filtro que Stock/precios). */
    proveedorId?: number;
  }): Promise<Producto[]> => {
    const query: Record<string, QueryParamValue> = {
      search: params?.search,
      grupoId: params?.grupoId,
      subgrupoId: params?.subgrupoId,
    };
    if (params?.esPersonalizado != null) {
      query.es_personalizado = params.esPersonalizado ? 'true' : 'false';
    }
    if (params?.proveedorId != null && params.proveedorId > 0) {
      query.proveedor_id = params.proveedorId;
    }
    const response = await apiRequest<PaginatedResponse<Producto> | Producto[]>('/productos/', {
      params: query,
    });
    return Array.isArray(response) ? response : response.results;
  },

  listPaginated: async (params?: {
    search?: string;
    grupoId?: number;
    subgrupoId?: number;
    esPersonalizado?: boolean;
    proveedorId?: number;
  } & PaginationParams): Promise<PaginatedResponse<Producto>> => {
    const { esPersonalizado, proveedorId, ...rest } = params || {};
    const query: Record<string, QueryParamValue> = { ...rest };
    if (esPersonalizado != null) {
      query.es_personalizado = esPersonalizado ? 'true' : 'false';
    }
    if (proveedorId != null && proveedorId > 0) {
      query.proveedor_id = proveedorId;
    }
    return apiRequest<PaginatedResponse<Producto>>('/productos/', { params: query });
  },

  get: async (codigo: string): Promise<Producto> => {
    return apiRequest<Producto>(`/productos/${encodeURIComponent(codigo)}/`);
  },

  create: async (data: Omit<Producto, 'id' | 'precioFinal'> & { precioFinal?: number; precio_final?: number }): Promise<Producto> => {
    // Convert camelCase to snake_case for Django backend
    const g = data.grupo ?? data.grupoId;
    const s = data.subgrupo ?? data.subgrupoId;
    const snakeCaseData: Record<string, unknown> = {
      codigo: data.codigo,
      nombre: data.nombre,
      precio_base: data.precioBase,
      iva_porcentaje: data.ivaPorcentaje,
      ganancia_porcentaje: data.gananciaPorcentaje,
      unidad_medida: data.unidadMedida,
      proveedor: data.proveedor || null,
      activo: data.activo,
      grupo: g !== undefined && g !== null && Number(g) > 0 ? Number(g) : null,
      subgrupo: s !== undefined && s !== null && Number(s) > 0 ? Number(s) : null,
    };
    if (data.precioFinal !== undefined) snakeCaseData.precio_final = data.precioFinal;
    if (data.precio_final !== undefined) snakeCaseData.precio_final = data.precio_final;
    return apiRequest<Producto>('/productos/', {
      method: 'POST',
      body: JSON.stringify(snakeCaseData),
    });
  },

  update: async (codigo: string, data: Partial<Producto>): Promise<Producto> => {
    // Convert camelCase to snake_case for Django backend
    const snakeCaseData: Record<string, any> = {};
    if (data.codigo !== undefined) snakeCaseData.codigo = data.codigo;
    if (data.nombre !== undefined) snakeCaseData.nombre = data.nombre;
    if (data.precioBase !== undefined) snakeCaseData.precio_base = data.precioBase;
    if (data.precio_base !== undefined) snakeCaseData.precio_base = data.precio_base;
    if (data.precioFinal !== undefined) snakeCaseData.precio_final = data.precioFinal;
    if (data.precio_final !== undefined) snakeCaseData.precio_final = data.precio_final;
    if (data.ivaPorcentaje !== undefined) snakeCaseData.iva_porcentaje = data.ivaPorcentaje;
    if (data.gananciaPorcentaje !== undefined) snakeCaseData.ganancia_porcentaje = data.gananciaPorcentaje;
    if (data.unidadMedida !== undefined) snakeCaseData.unidad_medida = data.unidadMedida;
    if (data.proveedor !== undefined) snakeCaseData.proveedor = data.proveedor;
    if (data.activo !== undefined) snakeCaseData.activo = data.activo;
    const g = data.grupo ?? data.grupoId;
    const s = data.subgrupo ?? data.subgrupoId;
    if (g !== undefined) snakeCaseData.grupo = g !== null && Number(g) > 0 ? Number(g) : null;
    if (s !== undefined) snakeCaseData.subgrupo = s !== null && Number(s) > 0 ? Number(s) : null;

    return apiRequest<Producto>(`/productos/${encodeURIComponent(codigo)}/`, {
      method: 'PATCH',
      body: JSON.stringify(snakeCaseData),
    });
  },

  delete: async (codigo: string): Promise<void> => {
    return apiRequest<void>(`/productos/${encodeURIComponent(codigo)}/`, {
      method: 'DELETE',
    });
  },

  ajustePreciosProveedor: async (data: {
    proveedorId: number;
    porcentaje: number;
    dryRun?: boolean;
  }): Promise<{
    proveedorId?: number;
    proveedor_id?: number;
    porcentaje: string;
    count?: number;
    updated?: number;
    muestra?: Array<{
      codigo: string;
      nombre: string;
      precioBase?: string;
      precio_base?: string;
      precioBaseNuevo?: string;
      precio_base_nuevo?: string;
      precioFinal?: string;
      precio_final?: string;
      precioFinalNuevo?: string;
      precio_final_nuevo?: string;
    }>;
  }> => {
    return apiRequest('/productos/ajuste-precios-proveedor/', {
      method: 'POST',
      body: JSON.stringify({
        proveedor_id: data.proveedorId,
        porcentaje: data.porcentaje,
        dry_run: data.dryRun === true,
      }),
    });
  },

  desactivarPersonalizados: async (data: {
    alcance: "todos" | "proveedor";
    proveedorId?: number;
    dryRun?: boolean;
  }): Promise<{
    alcance: string;
    proveedorId?: number | null;
    proveedor_id?: number | null;
    count?: number;
    updated?: number;
  }> => {
    return apiRequest("/productos/desactivar-personalizados/", {
      method: "POST",
      body: JSON.stringify({
        alcance: data.alcance,
        ...(data.proveedorId ? { proveedor_id: data.proveedorId } : {}),
        dry_run: data.dryRun === true,
      }),
    });
  },

  borrarPorProveedor: async (data: {
    proveedorId: number;
    confirm?: boolean;
    dryRun?: boolean;
    modo?: "soft" | "hard";
  }): Promise<{
    total: number;
    deleted: number;
    wouldDelete?: number;
    would_delete?: number;
    blocked: number;
    deletedCodigos?: string[];
    deleted_codigos?: string[];
    blockedItems?: Array<{ codigo: string; motivos: string[] }>;
    blocked_items?: Array<{ codigo: string; motivos: string[] }>;
    dryRun?: boolean;
    dry_run?: boolean;
    modo?: "soft" | "hard";
  }> => {
    return apiRequest('/productos/borrar-por-proveedor/', {
      method: 'POST',
      body: JSON.stringify({
        proveedor_id: data.proveedorId,
        confirm: data.confirm === true,
        dry_run: data.dryRun === true,
        modo: data.modo ?? "soft",
      }),
    });
  },

  /**
   * H.A.5 — orquestador Producto personalizado + config + stock.
   * 201 created | 409 config_hash_conflict (existente) | 400 validación.
   */
  createPersonalizado: async (
    payload: ProductoPersonalizadoCreatePayload,
  ): Promise<ProductoPersonalizadoCreated> => {
    // DRF camel-case middleware acepta camelCase; enviamos keys de contrato FE.
    const body: Record<string, unknown> = {
      selecciones: payload.selecciones,
    };
    if (payload.seleccionesUnificar) body.seleccionesUnificar = payload.seleccionesUnificar;
    if (payload.proveedorId != null) body.proveedorId = payload.proveedorId;
    if (payload.proveedorData != null) body.proveedorData = payload.proveedorData;
    if (payload.reactivateProveedor != null) {
      body.reactivateProveedor = payload.reactivateProveedor;
    }
    return apiRequest<ProductoPersonalizadoCreated>('/productos/personalizado/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  getPersonalizadoByHash: async (
    configHash: string,
  ): Promise<ProductoPersonalizadoLookup> => {
    return apiRequest<ProductoPersonalizadoLookup>('/productos/personalizado/por-hash/', {
      params: { hash: configHash },
    });
  },

  getNextCodigo: async (): Promise<{ codigo: string }> => {
    // This might be a custom action, or we handle it in frontend if backend doesn't provide
    // For now assuming backend doesn't exist, we might need to implement logic or just let user type
    // If backend has endpoint:
    // return apiRequest<{ codigo: string }>('/productos/next_codigo/'); 
    // Since we didn't implement that specific endpoint in checking backend/app/productos/views.py yet (it was standard ViewSet),
    // let's stick to standard CRUD for now. User might need to enter code manually or we generate loosely.
    // Actually, looking at the previous viewed code, we didn't see a next-codigo endpoint.
    // We will leave this one unimplemented or simple.
    throw new Error('API not implemented yet');
  },
};

/** Payload H.A.5 — selecciones del configurador + proveedor opcional. */
export interface ProductoPersonalizadoCreatePayload {
  selecciones: Record<string, unknown>;
  seleccionesUnificar?: Record<string, unknown> | Record<string, unknown>[];
  proveedorId?: number;
  proveedorData?: Record<string, unknown>;
  reactivateProveedor?: boolean;
}

export interface ProductoPersonalizadoConflict {
  code: 'config_hash_conflict';
  detail: string;
  configHash?: string;
  existente: {
    id: number;
    codigo: string;
    nombre: string;
    precioBase?: string | null;
    precioFinal?: string | null;
    esPersonalizado?: boolean;
    configHash?: string | null;
    proveedorId?: number | null;
  };
}

export interface ProductoPersonalizadoCreated {
  created: true;
  configHash: string;
  canonical?: Record<string, unknown>;
  precioFinal?: string | null;
  producto: {
    id: number;
    codigo: string;
    nombre: string;
    descripcion?: string;
    precioBase: string;
    precioFinal?: string | null;
    ivaPorcentaje?: string;
    esPersonalizado: boolean;
    origenConfigurador?: string | null;
    configHash?: string | null;
    proveedorId?: number | null;
    activo?: boolean;
  };
  configuracion?: {
    id: number;
    schemaVersion?: number;
    origen?: string;
    seleccionesCanonicas?: Record<string, string>;
    snapshotLabels?: Record<string, unknown>;
    descripcionGenerada?: string;
    stockInicial?: string;
    sucursalId?: number | null;
    depositoId?: number | null;
  } | null;
  stock?: {
    id: number;
    productoId: number;
    sucursalId: number;
    depositoId: number;
    cantidad: string;
  } | null;
}

export interface ProductoPersonalizadoLookup {
  producto: {
    id: number;
    codigo: string;
    nombre: string;
    precioBase: string;
    precioFinal?: string | null;
    esPersonalizado: boolean;
    origenConfigurador?: string | null;
    configHash?: string | null;
    proveedorId?: number | null;
  };
  configuracion?: {
    seleccionesCanonicas?: Record<string, string>;
    snapshotLabels?: Record<string, unknown>;
    descripcionGenerada?: string;
    origen?: string;
  } | null;
}

// ============================================================================
// CONFIGURADOR API (H.A — catálogo administrable)
// ============================================================================

/** Campo del catálogo (DRF camelCase). */
export interface ConfiguradorCampo {
  id: number;
  key: string;
  label: string;
  tipo: string;
  orden: number;
  activo: boolean;
  sourceId?: string | null;
  esGrupoVirtual?: boolean;
  esOperativo?: boolean;
  esRaiz?: boolean;
  tecnico?: boolean;
  labelProvisional?: boolean;
  mostrarEtiquetaEnDescripcion?: boolean;
  aplicaFavre?: boolean;
  aplicaOtras?: boolean;
  manualPermitidoFavre?: boolean;
  manualPermitidoOtras?: boolean;
  requeridoSiAplicableFavre?: boolean;
  requeridoSiAplicableOtras?: boolean;
  siempreRequerido?: boolean;
  valorFijo?: string | number | null;
  metadata?: Record<string, unknown>;
  /** Perfiles con PerfilCampo (membresía real). */
  membershipPerfiles?: string[];
  /** Visible/oculto por perfil (PerfilCampo.activo). */
  visiblePorPerfil?: Record<string, boolean>;
  createdAt?: string;
  updatedAt?: string;
}

export interface ConfiguradorOpcion {
  id: number;
  campo?: number;
  campoKey: string;
  key: string;
  label: string;
  orden: number;
  activo: boolean;
  sourceId?: string | null;
  proveedor?: number | null;
  metadata?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
}

export interface ConfiguradorDependencia {
  id: number;
  parentCampo?: number | null;
  parentCampoKey?: string | null;
  parentOpcion?: number | null;
  parentOpcionKey?: string | null;
  parentOpcionDetail?: {
    id: number;
    key: string;
    campoKey: string;
    label: string;
  } | null;
  childCampo?: number;
  childCampoKey: string;
  condicion: 'option_selected' | 'field_any' | 'value_gt_zero' | 'always' | string;
  /** null = compartida; valor = exclusiva de esa rama. */
  perfil?: 'favre' | 'otras' | null;
  activo: boolean;
  orden: number;
  /** DNF: mismo grupoId = AND; distintos = OR. */
  grupoId?: string | null;
  metadata?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
}

export interface ConfiguradorArbolOpcion {
  id: number;
  key: string;
  label: string;
  orden: number;
  activo: boolean;
  sourceId?: string | null;
  proveedorId?: number | null;
}

export interface ConfiguradorArbolDependencia {
  id: number;
  condicion: string;
  perfil?: 'favre' | 'otras' | null;
  grupoId?: string | null;
  parentOpcionKey?: string | null;
  childCampoKey: string;
}

export interface ConfiguradorArbolCampo {
  id: number;
  key: string;
  label: string;
  tipo: string;
  orden: number;
  activo: boolean;
  sourceId?: string | null;
  esGrupoVirtual?: boolean;
  esOperativo?: boolean;
  esRaiz?: boolean;
  tecnico?: boolean;
  labelProvisional?: boolean;
  mostrarEtiquetaEnDescripcion?: boolean;
  aplicaFavre?: boolean;
  aplicaOtras?: boolean;
  manualPermitidoFavre?: boolean;
  manualPermitidoOtras?: boolean;
  requeridoSiAplicableFavre?: boolean;
  requeridoSiAplicableOtras?: boolean;
  siempreRequerido?: boolean;
  valorFijo?: string | null;
  opciones: ConfiguradorArbolOpcion[];
  dependencias: ConfiguradorArbolDependencia[];
}

export interface ConfiguradorArbol {
  campos: ConfiguradorArbolCampo[];
  totales: { campos: number; opciones: number; dependencias: number };
}

/** Producto que referencia un campo/opción del Configurador. */
export interface ConfiguradorRemovalUsage {
  id: number;
  codigo: string;
  nombre: string;
  origenConfigurador?: string | null;
}

/** Preview de Quitar: acción prevista sin mutar. */
export interface ConfiguradorRemovalPreview {
  inUse: boolean;
  count: number;
  action: 'delete' | 'deactivate' | 'membership_removed';
  usages: ConfiguradorRemovalUsage[];
  remainingPerfiles?: string[];
}

/** Resultado de DELETE auto soft/hard. */
export interface ConfiguradorRemovalResult {
  action: 'deleted' | 'deactivated' | 'membership_removed';
  inUse: boolean;
  count: number;
  usages?: ConfiguradorRemovalUsage[];
  remainingPerfiles?: string[];
}

export type ConfiguradorCampoWrite = Partial<
  Omit<ConfiguradorCampo, 'id' | 'createdAt' | 'updatedAt'>
> & { key?: string; label?: string; tipo?: string; visible?: boolean };

export type ConfiguradorOpcionWrite = {
  campoKey?: string;
  key?: string;
  label?: string;
  orden?: number;
  activo?: boolean;
  sourceId?: string | null;
  proveedor?: number | null;
  metadata?: Record<string, unknown>;
};

export type ConfiguradorDependenciaWrite = {
  parentCampoKey?: string | null;
  parentOpcionKey?: string | null;
  childCampoKey?: string;
  condicion?: string;
  perfil?: 'favre' | 'otras' | null;
  activo?: boolean;
  orden?: number;
  /** DNF: reutilizar UUID para AND en el mismo grupo; omitir = nuevo grupo OR. */
  grupoId?: string | null;
  metadata?: Record<string, unknown>;
};

async function configuradorListAll<T>(
  endpoint: string,
  params?: Record<string, QueryParamValue>,
): Promise<T[]> {
  const response = await apiRequest<PaginatedResponse<T> | T[]>(endpoint, {
    params: { page_size: 500, ...params },
  });
  if (Array.isArray(response)) return response;
  return response.results ?? [];
}

export const configuradorAPI = {
  listCampos: async (params?: {
    includeInactive?: boolean;
    search?: string;
    tipo?: string;
    activo?: boolean;
  }): Promise<ConfiguradorCampo[]> => {
    return configuradorListAll<ConfiguradorCampo>('/configurador/campos/', {
      include_inactive: params?.includeInactive ? '1' : undefined,
      search: params?.search,
      tipo: params?.tipo,
      activo: params?.activo,
    });
  },

  getCampo: async (id: number): Promise<ConfiguradorCampo> => {
    return apiRequest<ConfiguradorCampo>(`/configurador/campos/${id}/`);
  },

  createCampo: async (data: ConfiguradorCampoWrite): Promise<ConfiguradorCampo> => {
    return apiRequest<ConfiguradorCampo>('/configurador/campos/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateCampo: async (
    id: number,
    data: ConfiguradorCampoWrite,
    opts?: { perfil?: string },
  ): Promise<ConfiguradorCampo> => {
    const { key: _k, ...payload } = data;
    return apiRequest<ConfiguradorCampo>(`/configurador/campos/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
      params: opts?.perfil ? { perfil: opts.perfil } : undefined,
    });
  },

  /** Preview soft vs hard: GET .../{id}/uso/ → { in_use, count, action }. */
  usoCampo: async (
    id: number,
    opts?: { perfil?: string },
  ): Promise<ConfiguradorRemovalPreview> => {
    return apiRequest<ConfiguradorRemovalPreview>(`/configurador/campos/${id}/uso/`, {
      params: opts?.perfil ? { perfil: opts.perfil } : undefined,
    });
  },

  /**
   * Quitar campo: hard delete si libre, soft-deactivate si en uso.
   * Con perfil de rama: saca membresía de ese perfil si el otro lo sigue usando.
   * Respuesta: { action: 'deleted'|'deactivated'|'membership_removed', in_use, count }.
   */
  quitarCampo: async (
    id: number,
    opts?: { perfil?: string },
  ): Promise<ConfiguradorRemovalResult> => {
    return apiRequest<ConfiguradorRemovalResult>(`/configurador/campos/${id}/`, {
      method: 'DELETE',
      params: opts?.perfil ? { perfil: opts.perfil } : undefined,
    });
  },

  listOpciones: async (params?: {
    includeInactive?: boolean;
    campoKey?: string;
    campo?: number;
    search?: string;
    activo?: boolean;
  }): Promise<ConfiguradorOpcion[]> => {
    return configuradorListAll<ConfiguradorOpcion>('/configurador/opciones/', {
      include_inactive: params?.includeInactive ? '1' : undefined,
      campo_key: params?.campoKey,
      campo: params?.campo,
      search: params?.search,
      activo: params?.activo,
    });
  },

  createOpcion: async (
    data: ConfiguradorOpcionWrite,
    opts?: { perfil?: string },
  ): Promise<ConfiguradorOpcion> => {
    return apiRequest<ConfiguradorOpcion>('/configurador/opciones/', {
      method: 'POST',
      body: JSON.stringify(data),
      params: opts?.perfil ? { perfil: opts.perfil } : undefined,
    });
  },

  updateOpcion: async (
    id: number,
    data: ConfiguradorOpcionWrite,
    opts?: { perfil?: string },
  ): Promise<ConfiguradorOpcion> => {
    const { key: _k, campoKey: _c, ...payload } = data;
    return apiRequest<ConfiguradorOpcion>(`/configurador/opciones/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
      params: opts?.perfil ? { perfil: opts.perfil } : undefined,
    });
  },

  usoOpcion: async (id: number): Promise<ConfiguradorRemovalPreview> => {
    return apiRequest<ConfiguradorRemovalPreview>(`/configurador/opciones/${id}/uso/`);
  },

  /** Quitar opción: hard si libre, soft si en uso. */
  quitarOpcion: async (id: number): Promise<ConfiguradorRemovalResult> => {
    return apiRequest<ConfiguradorRemovalResult>(`/configurador/opciones/${id}/`, {
      method: 'DELETE',
    });
  },

  listDependencias: async (params?: {
    includeInactive?: boolean;
    parentCampoKey?: string;
    childCampoKey?: string;
    activo?: boolean;
  }): Promise<ConfiguradorDependencia[]> => {
    return configuradorListAll<ConfiguradorDependencia>('/configurador/dependencias/', {
      include_inactive: params?.includeInactive ? '1' : undefined,
      parent_campo_key: params?.parentCampoKey,
      child_campo_key: params?.childCampoKey,
      activo: params?.activo,
    });
  },

  createDependencia: async (data: ConfiguradorDependenciaWrite): Promise<ConfiguradorDependencia> => {
    return apiRequest<ConfiguradorDependencia>('/configurador/dependencias/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateDependencia: async (
    id: number,
    data: ConfiguradorDependenciaWrite,
  ): Promise<ConfiguradorDependencia> => {
    return apiRequest<ConfiguradorDependencia>(`/configurador/dependencias/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  usoDependencia: async (id: number): Promise<ConfiguradorRemovalPreview> => {
    return apiRequest<ConfiguradorRemovalPreview>(
      `/configurador/dependencias/${id}/uso/`,
    );
  },

  /** Quitar dependencia: siempre hard delete. */
  quitarDependencia: async (id: number): Promise<ConfiguradorRemovalResult> => {
    return apiRequest<ConfiguradorRemovalResult>(`/configurador/dependencias/${id}/`, {
      method: 'DELETE',
    });
  },

  getArbol: async (includeInactive = false): Promise<ConfiguradorArbol> => {
    return apiRequest<ConfiguradorArbol>('/configurador/arbol/', {
      params: includeInactive ? { include_inactive: '1' } : undefined,
    });
  },

  evaluar: async (
    selecciones: Record<string, unknown>,
    includeInactive = false,
    perfilId?: number | string | null,
  ): Promise<{
    rama?: string | null;
    perfil_id?: number | null;
    perfil_key?: string | null;
    source?: 'legacy' | 'perfil' | string;
    campos: Array<Record<string, unknown>>;
    errores: unknown[];
    valido: boolean;
  }> => {
    const body: Record<string, unknown> = { selecciones, includeInactive };
    if (perfilId !== undefined && perfilId !== null && perfilId !== '') {
      body.perfil_id = perfilId;
    }
    return apiRequest('/configurador/evaluar/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  precioPreview: async (precio: string | number): Promise<{
    precio: string;
    ivaPorcentaje: string;
    precioFinalConIva: string;
  }> => {
    return apiRequest('/configurador/precio-preview/', {
      method: 'POST',
      body: JSON.stringify({ precio }),
    });
  },
};

// ============================================================================
// STOCK API
// ============================================================================

export interface Stock {
  id?: number;
  producto: number;
  productoCodigo?: string;
  productoNombre?: string;
  productoPrecioBase?: number;
  productoIva?: number;
  productoPrecioFinal?: number;
  productoProveedor?: string | number;
  productoActivo?: boolean;
  deposito: number;
  depositoNombre?: string;
  /** @deprecated F5: la pila es por depósito; sucursales solo autorizan. */
  sucursal?: number;
  sucursalNombre?: string;
  sucursales?: number[];
  sucursalesNombres?: string[];
  cantidad: number;
  stockMinimo: number;
  stockMaximo?: number;
  ubicacion?: string;
  updatedAt?: string;
  unidadMedida?: string;
}

export interface StockMovimiento {
  id?: number;
  productoId: number;
  depositoId: number;
  tipo: string;
  cantidad: number;
  cantidadAnterior: number;
  cantidadNueva: number;
  referencia?: string;
  motivo?: string;
  usuarioId?: number;
  fecha: string;
}

export const stockAPI = {
  list: async (params?: {
    search?: string;
    deposito?: number;
    sucursal?: number;
    producto?: number;
    page_size?: number;
  }): Promise<Stock[]> => {
    // page_size alto por defecto: un producto puede tener N filas (multi-depósito).
    const response = await apiRequest<PaginatedResponse<Stock>>('/stock/', {
      params: { page_size: 500, ...params },
    });
    return response.results;
  },

  listPaginated: async (params?: { search?: string; depositoId?: number; sucursalId?: number } & PaginationParams): Promise<PaginatedResponse<Stock>> => {
    return apiRequest<PaginatedResponse<Stock>>('/stock/', { params });
  },

  get: async (id: number): Promise<Stock> => {
    return apiRequest<Stock>(`/stock/${id}/`);
  },

  create: async (data: Omit<Stock, 'id'>): Promise<Stock> => {
    return apiRequest<Stock>('/stock/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: number, data: Partial<Stock>): Promise<Stock> => {
    return apiRequest<Stock>(`/stock/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number): Promise<void> => {
    return apiRequest<void>(`/stock/${id}/`, {
      method: 'DELETE',
    });
  },

  ajustar: async (data: { productoId: number; depositoId: number; cantidad: number; motivo?: string }): Promise<void> => {
    return apiRequest<void>('/stock/ajustar/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  transferir: async (data: {
    productoId: number;
    origenDepositoId: number;
    destinoDepositoId: number;
    cantidad: number;
    motivo?: string
  }): Promise<void> => {
    return apiRequest<void>('/stock/transferir/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  listMovimientos: async (params?: {
    productoId?: number;
    depositoId?: number;
    fechaDesde?: string;
    fechaHasta?: string
  }): Promise<StockMovimiento[]> => {
    const response = await apiRequest<PaginatedResponse<StockMovimiento>>('/stock/movimientos/', { params });
    return response.results;
  },

  getAlerts: async (): Promise<{
    lowStock: { count: number; items: { codigo: string; nombre: string; cantidad: number; stockMinimo: number }[] };
    zeroStock: { count: number; items: { codigo: string; nombre: string; cantidad: number }[] };
  }> => {
    return apiRequest('/stock/alerts/');
  },
};

// ============================================================================
// VENTAS API
// ============================================================================

export interface Presupuesto {
  id?: number;
  numero: string;
  clienteId?: number;
  cliente?: { codigo: string; nombre: string; id?: number }; // Alias for backwards compat
  puntoVentaId?: number;
  usuarioId?: number;
  fecha: string;
  validoHasta?: string;
  subtotal: number;
  descuento: number;
  iva: number;
  total: number;
  estado: 'pendiente' | 'aceptado' | 'rechazado' | 'facturado';
  clienteEditable?: boolean;
  cliente_editable?: boolean;
  observaciones?: string;
  pdfUrl?: string;
  items: PresupuestoItem[];
  usuarioNombre?: string | null;
  usuario_nombre?: string | null;
}

export interface PresupuestoItem {
  id?: number;
  presupuestoId?: number;
  productoId?: number;
  producto?: number | null;
  codigoProducto: string;
  nombreProducto: string;
  codigo_producto?: string;
  nombre_producto?: string;
  cantidad: number;
  precioUnitario: number;
  precio_unitario?: number;
  descuento1: number;
  descuento2: number;
  subtotal: number;
  ivaPorcentaje: number;
  ivaImporte: number;
  total: number;
  orden: number;
  /** Personalizado diferido (sin Producto hasta la venta). */
  config_snapshot?: Record<string, unknown> | null;
  config_hash?: string | null;
  configSnapshot?: Record<string, unknown> | null;
  configHash?: string | null;
  combo_group?: string | null;
  comboGroup?: string | null;
}

export interface Factura {
  id?: number;
  numero: string;
  numeroDisplay: string;
  numeroVisible?: string;
  numeroFiscal?: number;
  estadoFiscal?: 'BORRADOR' | 'ENVIANDO' | 'INCIERTO' | 'AUTORIZADO' | 'RECHAZADO' | 'LOCAL' | 'NO_ENCONTRADO';
  talonario: number;
  numeroComprobante: number;
  tipoComprobante: string;
  tipoFactura: 'A' | 'B' | 'X';
  puntoVentaId?: number;
  sucursalId?: number;
  sucursal_id?: number;
  clienteId?: number;
  cliente?: any; // Nested object from serializer
  esConsumidorFinal: boolean;
  presupuestoId?: number;
  nroPresupuesto?: string;
  usuarioId?: number;
  usuario?: number | null;
  /** F3: username del usuario que confirmó/generó la factura. */
  usuarioUsername?: string | null;
  usuario_username?: string | null;
  /** F3: nombre visible (full name o username). */
  usuarioNombre?: string | null;
  usuario_nombre?: string | null;
  fecha: string;
  fechaVencimiento?: string;
  subtotal: number;
  descuento: number;
  iva: number;
  total: number; // Keep as number
  estado: string;
  cae?: string;
  fechaCae?: string;
  vencimientoCae?: string;
  metodoPago?: string;
  pagos?: Array<{ metodoPago?: string }>;
  observaciones?: string;
  pdfUrl?: string;
  /** true si ya hay un ajuste que bloquea otra NC. En CAE, NC fiscal o NC-X. */
  tieneNotaCredito?: boolean;
  /** NC-X interna ya emitida sobre esta factura CAE. También cierra la NC fiscal. */
  tieneNcX?: boolean;
  tiene_nc_x?: boolean;
  items: FacturaItem[];
}

export interface FacturaItem {
  id?: number;
  facturaId?: number;
  productoId?: number;
  codigoProducto: string;
  nombreProducto: string;
  cantidad: number;
  /** Unidades ya acreditadas en NCs previas (autorizadas / NC-X). */
  cantidadAcreditada?: number;
  /** Unidades aún acreditables: cantidad - cantidadAcreditada. */
  cantidadRestante?: number;
  /** Qty no entregada del remito abierto (facturada − entregada). Prefill NC parcial. */
  cantidadPendienteEntrega?: number;
  cantidad_pendiente_entrega?: number;
  precioUnitario: number;
  descuento1: number;
  descuento2: number;
  subtotal: number;
  ivaPorcentaje: number;
  ivaImporte: number;
  total: number;
  orden: number;
  combo_group?: string | null;
  comboGroup?: string | null;
  /** Override visual PDF del combo (una línea). No pisa SKU de catálogo. */
  combo_nombre_pdf?: string | null;
  comboNombrePdf?: string | null;
}

export const ventasAPI = {
  // Presupuestos
  listPresupuestos: async (params?: { estado?: string; clienteId?: number }): Promise<Presupuesto[]> => {
    const response = await apiRequest<PaginatedResponse<Presupuesto>>('/presupuestos/', { params });
    return response.results;
  },

  listPresupuestosPage: async (
    params?: Record<string, QueryParamValue>,
  ): Promise<PaginatedResponse<Presupuesto>> => {
    return apiRequest<PaginatedResponse<Presupuesto>>('/presupuestos/', { params });
  },

  /** Recorre las páginas (tope 200) hasta traer la semana o la búsqueda completa. */
  listPresupuestosTodos: async (
    params?: Record<string, QueryParamValue>,
  ): Promise<Presupuesto[]> => {
    const todos: Presupuesto[] = [];
    let page = 1;
    while (page <= 50) {
      const response = await apiRequest<PaginatedResponse<Presupuesto>>('/presupuestos/', {
        params: { ...params, page, page_size: 200 },
      });
      todos.push(...(response.results || []));
      if (!response.next || (response.results || []).length === 0) break;
      page += 1;
    }
    return todos;
  },

  getPresupuesto: async (id: number): Promise<Presupuesto> => {
    return apiRequest<Presupuesto>(`/presupuestos/${id}/`);
  },

  createPresupuesto: async (data: Omit<Presupuesto, 'id'>): Promise<Presupuesto> => {
    return apiRequest<Presupuesto>('/presupuestos/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updatePresupuesto: async (id: number, data: Partial<Presupuesto>): Promise<Presupuesto> => {
    return apiRequest<Presupuesto>(`/presupuestos/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deletePresupuesto: async (id: number): Promise<void> => {
    return apiRequest<void>(`/presupuestos/${id}/`, {
      method: 'DELETE',
    });
  },

  /** Tacho de un renglón: si es combo, borra las dos patas. */
  eliminarPresupuestoItem: async (presupuestoId: number, itemId: number): Promise<Presupuesto> => {
    return apiRequest<Presupuesto>(`/presupuestos/${presupuestoId}/eliminar-item/`, {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId }),
    });
  },

  downloadPresupuestoPDF: async (id: number): Promise<File> => {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE_URL}/presupuestos/${id}/pdf/`, {
      method: 'GET',
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error(`Presupuesto PDF download failed: ${response.status}`);
    }
    return pdfFileFromResponse(response, "pre_comprobante.pdf");
  },

  // Facturas
  listFacturas: async (params?: { estado?: string; clienteId?: number; fechaDesde?: string; fechaHasta?: string }): Promise<Factura[]> => {
    const response = await apiRequest<PaginatedResponse<Factura>>('/facturas/', { params });
    return response.results;
  },

  /**
   * Página de facturas (incluye `next` para paginar). Filtros Django: `fecha__gte`, `fecha__lte`, `estado`, `cliente`.
   */
  listFacturasPage: async (params?: {
    estado?: string;
    cliente?: number;
    fecha?: string;
    fecha__gte?: string;
    fecha__lte?: string;
    tipo_comprobante?: string;
    search?: string;
    sucursal?: number;
    historial?: boolean | number | string;
    /** Con cliente: no recortar al mes. El listado operativo no lo manda. */
    sin_mes?: boolean | number | string;
    month?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<Factura>> => {
    return apiRequest<PaginatedResponse<Factura>>('/facturas/', { params });
  },

  /** Cantidad y total de comprobantes por cliente. meses corridos, o todos. */
  resumenComprobantesPorCliente: async (params?: {
    meses?: 1 | 3 | 6 | 12 | "todos";
  }): Promise<Array<{ codigo: string; cantidad: number; total: string | number }>> => {
    return apiRequest("/facturas/resumen-clientes/", {
      params: { meses: params?.meses ?? 1 },
    });
  },

  listCierreDias: async (params?: {
    year?: number;
    month?: number;
    sucursal?: number;
  }): Promise<Array<{
    fecha: string;
    cerrado: boolean;
    sucursalId?: number;
    sucursal_id?: number;
    tieneCierre?: boolean;
    tiene_cierre?: boolean;
    tieneFactura?: boolean;
    tiene_factura?: boolean;
  }>> => {
    return apiRequest('/facturas/cierre-dias/', { params });
  },

  getFechaVenta: async (params?: {
    fecha?: string;
    punto_venta?: number;
    sucursal?: number;
  }): Promise<{
    fecha_solicitada: string;
    fecha: string | null;
    dia_cerrado: boolean;
    sucursal: number | null;
  }> => {
    return apiRequest('/facturas/fecha-venta/', { params });
  },

  /**
   * Todas las facturas del filtro (paginación interna). Para libro IVA; no usar como sustituto del listado de ventas.
   */
  listFacturasAll: async (
    params?: { estado?: string; cliente?: number; fecha__gte?: string; fecha__lte?: string; tipo_comprobante?: string },
    pageSize: number = 2000
  ): Promise<Factura[]> => {
    const out: Factura[] = [];
    let page = 1;
    while (true) {
      const r = await apiRequest<PaginatedResponse<Factura>>('/facturas/', {
        params: { ...params, page, page_size: pageSize },
      });
      const batch = r.results || [];
      out.push(...batch);
      if (!r.next || batch.length === 0) break;
      page += 1;
    }
    return out;
  },

  getFactura: async (id: number): Promise<Factura> => {
    return apiRequest<Factura>(`/facturas/${id}/`);
  },

  getFacturaComplete: async (numero: string): Promise<any> => {
    return apiRequest<any>(`/facturas/${numero}/complete/`);
  },

  downloadFacturaPDF: async (id: number): Promise<File> => {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE_URL}/facturas/${id}/pdf/`, {
      method: 'GET',
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error(`PDF download failed: ${response.status}`);
    }
    return pdfFileFromResponse(response, "fa_comprobante.pdf");
  },

  createFactura: async (data: Omit<Factura, 'id'>): Promise<Factura> => {
    return apiRequest<Factura>('/facturas/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  confirmarFactura: async (id: number, data: {
    tipo: 'cae' | 'negro';
    tipoFactura?: string;
    enviarACuentaCorriente?: boolean;
    metodoPago?: string;
    saldoAplicado?: number;
    metodosPago?: Array<{
      metodoPago: string;
      importe: number;
      importeUSD?: number;
      tasaCambio?: number;
      chequeNumero?: string;
      chequeBanco?: string;
      chequeFecha?: string;
      chequeFechaCreacion?: string;
      chequeTipo?: string;
    }>;
  }): Promise<Factura> => {
    return apiRequest<Factura>(`/facturas/${id}/confirmar/`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Idempotency-Key': `factura:${id}:v1` },
    });
  },

  /** F3: stock por depósito de factura pendiente (antes de emitir; no crea remito). */
  previewEntregaDeposito: async (id: number): Promise<VentaRemitoOpcionesDeposito> => {
    return apiRequest<VentaRemitoOpcionesDeposito>(
      `/facturas/${id}/preview_entrega_deposito/`,
      { method: 'GET' },
    );
  },

  reconciliarFactura: async (id: number): Promise<{
    outcome: string;
    factura: Factura;
  }> => {
    return apiRequest<{ outcome: string; factura: Factura }>(
      `/facturas/${id}/reconciliar/`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  },

  getNextNumero: async (tipoFactura: 'A' | 'B' | 'X'): Promise<{ numero: string; numeroDisplay: string }> => {
    return apiRequest<{ numero: string; numeroDisplay: string }>('/facturas/next-numero/', {
      params: { tipo: tipoFactura },
    });
  },

  deleteFactura: async (id: number): Promise<void> => {
    return apiRequest<void>(`/facturas/${id}/`, {
      method: 'DELETE',
    });
  },

  /** Tacho de un renglón (pendiente): si es combo, borra las dos patas. */
  eliminarFacturaItem: async (facturaId: number, itemId: number): Promise<Factura> => {
    return apiRequest<Factura>(`/facturas/${facturaId}/eliminar-item/`, {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId }),
    });
  },

  /** Solo admin: renombrar nombre del artículo en un ítem de factura (no qty/precio). */
  updateFacturaItemNombre: async (
    facturaId: number,
    itemId: number,
    nombreProducto: string,
  ): Promise<Factura> => {
    return apiRequest<Factura>(`/facturas/${facturaId}/update-item-nombre/`, {
      method: 'PATCH',
      body: JSON.stringify({ itemId, nombreProducto }),
    });
  },

  createNotaCredito: async (
    facturaId: number,
    esCreditoCliente: boolean = false,
    items?: Array<{ id: number; cantidad: number }>
  ): Promise<Factura> => {
    const url = `/facturas/${facturaId}/nota_credito/?esCreditoCliente=${esCreditoCliente}`;
    const body: Record<string, any> = { esCreditoCliente };
    if (items && items.length > 0) {
      body.items = items;
    }
    const lineKey = items?.length
      ? items.map((i) => `${i.id}:${i.cantidad}`).sort().join('|')
      : 'full';
    // Mantener ≤80 chars (FiscalEmissionAttempt.idempotency_key).
    let digest = lineKey;
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const buf = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(lineKey),
      );
      digest = Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
        .slice(0, 20);
    } else {
      digest = String(lineKey.length) + ':' + lineKey.slice(0, 16);
    }
    return apiRequest<Factura>(url, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Idempotency-Key': `nc:${facturaId}:${esCreditoCliente ? 1 : 0}:${digest}`,
      },
    });
   },

  /** NC-X interna de una factura CAE. No llama a AFIP ni consume la NC fiscal. */
  createNotaCreditoX: async (
    facturaId: number,
    esCreditoCliente: boolean = false,
    items?: Array<{ id: number; cantidad: number }>
  ): Promise<Factura> => {
    const body: Record<string, any> = { esCreditoCliente };
    if (items && items.length > 0) {
      body.items = items;
    }
    return apiRequest<Factura>(`/facturas/${facturaId}/nota_credito_x/`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
};

// ============================================================================
// REPORTES API
// ============================================================================

export type ReportePeriodo = "dia" | "semana" | "mes" | "anio";

export interface ReportesRangoParams {
  periodo?: ReportePeriodo;
  fecha?: string;
  desde?: string;
  hasta?: string;
  sucursal_id?: number;
}

export interface ReporteVentasGeneralRow {
  tipo_comprobante: string;
  cantidad: number;
  total: string;
  total_bruto: string;
  ticket_promedio: string;
  /** Magnitud positiva de NC; solo presente en filas NC-* */
  total_nc_magnitud?: string;
}

export type ReporteVentasGeneralTipoFiltro =
  | "todos"
  | "A"
  | "B"
  | "X"
  | "NC-A"
  | "NC-B"
  | "NC-X";

export interface ReporteVentasGeneralRubroRow {
  grupo_id: number;
  grupo_nombre: string;
  cantidad: number;
  total: string;
  total_bruto: string;
  ticket_promedio: string;
}

export interface ReporteVentasGeneralRubroFiltro {
  id: number;
  codigo: string;
  nombre: string;
}

export interface ReporteVentasGeneralResponse {
  desde: string;
  hasta: string;
  meta: Record<string, unknown>;
  por_tipo: ReporteVentasGeneralRow[];
  por_rubro?: ReporteVentasGeneralRubroRow[];
  filtros?: { rubros?: ReporteVentasGeneralRubroFiltro[] };
  total_neto_periodo: string;
  /** Universo sin filtro de rubro (ítem 2). Igual a total_neto_periodo en Todos. */
  total_general: string;
  total_bruto: string;
  total_nc: string;
  ticket_promedio: string;
  cantidad_comprobantes: number;
}

export interface ReporteVentasPorItemRow {
  producto_id: number | null;
  codigo_producto: string;
  nombre_producto: string;
  cantidad_vendida: string;
  importe_total: string;
}

export interface ReporteVentasPorItemResponse {
  desde: string;
  hasta: string;
  meta: Record<string, unknown>;
  items: ReporteVentasPorItemRow[];
}

export interface ReporteVentasPorVendedorRow {
  usuario_id: number | null;
  usuario_username: string | null;
  usuario_label: string;
  total_vendido: string;
  comprobantes: number;
  ticket_promedio: string;
}

export interface ReporteVentasPorVendedorResponse {
  desde: string;
  hasta: string;
  meta: Record<string, unknown>;
  vendedores: ReporteVentasPorVendedorRow[];
}

export interface ReporteVentasPorUsuarioDetalleRow {
  id: number;
  fecha: string | null;
  numero: string;
  tipo_comprobante: string;
  cliente_nombre: string;
  usuario_label: string;
  estado: string;
  total: string;
}

export interface ReporteVentasPorUsuarioDetalleResponse {
  desde: string;
  hasta: string;
  usuario_id: number | null;
  usuario_username: string | null;
  usuario_label: string;
  totales: {
    total_vendido: string;
    comprobantes: number;
    ticket_promedio: string;
  };
  meta: Record<string, unknown>;
  facturas: ReporteVentasPorUsuarioDetalleRow[];
}

export interface ReportePresupuestosPorUsuarioRow {
  usuario_id: number | null;
  usuario_username: string | null;
  usuario_label: string;
  creados: number;
  usados: number;
  cancelados: number;
}

export interface ReportePresupuestosPorUsuarioResponse {
  desde: string;
  hasta: string;
  meta: Record<string, unknown>;
  usuarios: ReportePresupuestosPorUsuarioRow[];
}

export interface ReportePresupuestosPorUsuarioDetalleRow {
  id: number;
  fecha: string | null;
  numero: string;
  cliente_nombre: string;
  usuario_label: string;
  estado: string;
  total: string;
  eliminado: boolean;
}

export interface ReportePresupuestosPorUsuarioDetalleResponse {
  desde: string;
  hasta: string;
  usuario_id: number | null;
  usuario_username: string | null;
  usuario_label: string;
  totales: {
    creados: number;
    usados: number;
    cancelados: number;
  };
  meta: Record<string, unknown>;
  presupuestos: ReportePresupuestosPorUsuarioDetalleRow[];
}

export interface ReporteStockRowGeneral {
  producto_id: number;
  codigo: string;
  nombre: string;
  cantidad_total: string;
}

export interface ReporteStockRowFabrica {
  proveedor_id: number | null;
  proveedor_codigo: string;
  proveedor_nombre: string;
  cantidad_total: string;
}

export interface ReporteStockResponse {
  vista: "general" | "fabrica";
  meta: Record<string, unknown>;
  rows: ReporteStockRowGeneral[] | ReporteStockRowFabrica[];
}

export interface ReportePreciosRow {
  producto_id: number;
  codigo: string;
  nombre: string;
  grupo_id: number | null;
  grupo_nombre: string;
  proveedor_id: number | null;
  proveedor_nombre: string;
  precio_base: string;
  ganancia_porcentaje: string;
  iva_porcentaje: string;
  precio_final: string;
  activo: boolean;
  es_personalizado: boolean;
}

export interface ReportePreciosResponse {
  meta: Record<string, unknown> & {
    total_filas?: number;
    page?: number;
    page_size?: number;
    export?: boolean;
  };
  rows: ReportePreciosRow[];
}

export interface ReporteClienteDeudorRow {
  codigo: string;
  nombre: string;
  saldo: string;
}

export interface ReporteClientesDeudoresResponse {
  desde: string;
  hasta: string;
  modo: string;
  meta: Record<string, unknown>;
  clientes: ReporteClienteDeudorRow[];
}

export interface ReporteRemitoAbiertoRow {
  linea_id: number;
  remito_id: number;
  remito_numero: string;
  cliente_codigo: string;
  cliente_nombre: string;
  codigo: string;
  descripcion: string;
  cantidad: string;
  entregado: string;
  pendiente: string;
  dias: number;
  tipo: string;
  fecha: string | null;
  usuario_venta: string;
  saldo_cliente: string;
  estado_remito: string;
}

export interface ReporteRemitosAbiertosResponse {
  generado: string;
  meta: Record<string, unknown>;
  totales: { filas: number };
  rows: ReporteRemitoAbiertoRow[];
}

export interface ReporteChequeRow {
  id: number;
  codigo: string;
  numero_cheque: string;
  banco: string;
  fecha_emision: string;
  fecha_cobro: string | null;
  tipo: string | null;
  origen: string | null;
  importe: string;
  moneda: string;
  cobrado: boolean;
  estado: string;
  emisor: string | null;
}

export interface ReporteChequesResponse {
  desde: string;
  hasta: string;
  meta: Record<string, unknown>;
  cheques: ReporteChequeRow[];
}

export interface ReporteMovimientoCajaFiltroTipo {
  id: number;
  codigo: string;
  nombre: string;
  sentido: string;
}

export interface ReporteMovimientoCajaFiltroUsuario {
  id: number;
  usuario_label: string;
  usuario_username: string | null;
}

export interface ReporteMovimientoCajaRow {
  id: number;
  codigo: string;
  fecha: string | null;
  tipo_movimiento_id: number | null;
  tipo_movimiento_nombre: string;
  sentido: string;
  /** Destino del filtro «Asociar a usuario» (compat). */
  usuario_id: number | null;
  usuario_label: string;
  usuario_username: string | null;
  /** Quién cargó el movimiento (created_by). Columna Usuario del informe. */
  created_by_id?: number | null;
  created_by_label?: string;
  /** Destinatario. Columna «Usuario destino», entre Usuario e Importe. */
  usuario_destino_id?: number | null;
  usuario_destino_label?: string;
  usuario_destino_username?: string | null;
  importe: string;
  importe_signed: string;
  observaciones: string;
}

export interface ReporteMovimientosCajaResponse {
  desde: string;
  hasta: string;
  totales: {
    cantidad: number;
    total_ingresos: string;
    total_egresos: string;
    total_neto: string;
  };
  filtros: {
    tipos: ReporteMovimientoCajaFiltroTipo[];
    usuarios: ReporteMovimientoCajaFiltroUsuario[];
  };
  meta: Record<string, unknown>;
  movimientos: ReporteMovimientoCajaRow[];
}

export interface ReporteValorizacionFiltroSucursal {
  id: number;
  codigo: string;
  nombre: string;
}

export interface ReporteValorizacionFiltroDeposito {
  id: number;
  codigo: string;
  nombre: string;
  sucursal_ids: number[];
}

export interface ReporteValorizacionStockRow {
  producto_id: number;
  codigo: string;
  nombre: string;
  deposito_id: number;
  deposito_codigo: string;
  deposito_nombre: string;
  sucursales: string[];
  cantidad: string;
  precio_base: string;
  valor: string;
}

export interface ReporteValorizacionStockResponse {
  totales: {
    filas: number;
    cantidad_total: string;
    valor_total: string;
  };
  filtros: {
    sucursales: ReporteValorizacionFiltroSucursal[];
    depositos: ReporteValorizacionFiltroDeposito[];
  };
  meta: Record<string, unknown> & {
    campo_valor?: string;
    campo_valor_label?: string;
    excluye_reservado?: boolean;
  };
  rows: ReporteValorizacionStockRow[];
}

export interface DashboardPendienteRemitoVtas {
  id: number;
  numero: string;
  clienteNombre: string;
  estado: string;
  fecha?: string | null;
}

export interface DashboardPendienteRemitoCompra {
  id: number;
  numero: string;
  proveedorNombre: string;
  estado: string;
  fecha?: string | null;
}

export interface DashboardPendienteFactura {
  id: number;
  numero: string;
  numeroInterno?: string;
  clienteNombre: string;
  estado: string;
  tipoComprobante?: string;
  fecha?: string | null;
}

export interface DashboardPendientesResponse {
  meta: Record<string, unknown>;
  remitosVtas: { count: number; items: DashboardPendienteRemitoVtas[] };
  remitosCompras: { count: number; items: DashboardPendienteRemitoCompra[] };
  facturas: { count: number; items: DashboardPendienteFactura[] };
}

export interface DashboardSerieResponse {
  meses: number;
  desde: string;
  hasta: string;
  categories: string[];
  ventas: string[];
  compras: string[];
  ventasYoy: string[] | null;
  comprasYoy: string[] | null;
  meta: Record<string, unknown>;
}

function normalizeDashboardPendientes(raw: Record<string, unknown>): DashboardPendientesResponse {
  const remitosVtas = (raw.remitos_vtas || raw.remitosVtas || {}) as Record<string, unknown>;
  const remitosCompras = (raw.remitos_compras || raw.remitosCompras || {}) as Record<string, unknown>;
  const facturas = (raw.facturas || {}) as Record<string, unknown>;

  const mapVtas = (items: unknown[]): DashboardPendienteRemitoVtas[] =>
    (items || []).map((it: any) => ({
      id: it.id,
      numero: it.numero,
      clienteNombre: it.cliente_nombre ?? it.clienteNombre ?? "Sin cliente",
      estado: it.estado,
      fecha: it.fecha ?? null,
    }));

  const mapCompras = (items: unknown[]): DashboardPendienteRemitoCompra[] =>
    (items || []).map((it: any) => ({
      id: it.id,
      numero: it.numero,
      proveedorNombre: it.proveedor_nombre ?? it.proveedorNombre ?? "Sin proveedor",
      estado: it.estado,
      fecha: it.fecha ?? null,
    }));

  const mapFacturas = (items: unknown[]): DashboardPendienteFactura[] =>
    (items || []).map((it: any) => ({
      id: it.id,
      numero: it.numero,
      numeroInterno: it.numero_interno ?? it.numeroInterno,
      clienteNombre: it.cliente_nombre ?? it.clienteNombre ?? "Sin cliente",
      estado: it.estado,
      tipoComprobante: it.tipo_comprobante ?? it.tipoComprobante,
      fecha: it.fecha ?? null,
    }));

  return {
    meta: (raw.meta as Record<string, unknown>) || {},
    remitosVtas: {
      count: Number(remitosVtas.count || 0),
      items: mapVtas((remitosVtas.items as unknown[]) || []),
    },
    remitosCompras: {
      count: Number(remitosCompras.count || 0),
      items: mapCompras((remitosCompras.items as unknown[]) || []),
    },
    facturas: {
      count: Number(facturas.count || 0),
      items: mapFacturas((facturas.items as unknown[]) || []),
    },
  };
}

function normalizeDashboardSerie(raw: Record<string, unknown>): DashboardSerieResponse {
  const asStrArr = (v: unknown): string[] =>
    Array.isArray(v) ? v.map((x) => String(x ?? "0")) : [];
  const yoyOrNull = (v: unknown): string[] | null => (v == null ? null : asStrArr(v));
  return {
    meses: Number(raw.meses || 0),
    desde: String(raw.desde || ""),
    hasta: String(raw.hasta || ""),
    categories: asStrArr(raw.categories),
    ventas: asStrArr(raw.ventas),
    compras: asStrArr(raw.compras),
    ventasYoy: yoyOrNull(raw.ventas_yoy ?? raw.ventasYoy),
    comprasYoy: yoyOrNull(raw.compras_yoy ?? raw.comprasYoy),
    meta: (raw.meta as Record<string, unknown>) || {},
  };
}

export const reportesAPI = {
  ventasGeneral: async (
    params: ReportesRangoParams & {
      tipo_comprobante?: ReporteVentasGeneralTipoFiltro;
      grupo_id?: number;
      desglose?: "rubro";
    }
  ): Promise<ReporteVentasGeneralResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/ventas-general/", { params });
    return normalizeReporteVentasGeneral(raw);
  },
  ventasPorItem: async (
    params: ReportesRangoParams & { order?: "importe" | "cantidad" }
  ): Promise<ReporteVentasPorItemResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/ventas-por-item/", { params });
    return normalizeReporteVentasPorItem(raw);
  },
  ventasPorVendedor: async (params: ReportesRangoParams): Promise<ReporteVentasPorVendedorResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/ventas-por-vendedor/", { params });
    return normalizeReporteVentasPorVendedor(raw);
  },
  ventasPorUsuario: async (params: ReportesRangoParams): Promise<ReporteVentasPorVendedorResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/ventas-por-usuario/", { params });
    return normalizeReporteVentasPorVendedor(raw);
  },
  ventasPorUsuarioDetalle: async (
    params: ReportesRangoParams & { usuario_id: number | "sin_usuario" }
  ): Promise<ReporteVentasPorUsuarioDetalleResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/ventas-por-usuario/detalle/", {
      params: {
        ...params,
        usuario_id: params.usuario_id === "sin_usuario" ? "sin_usuario" : params.usuario_id,
      },
    });
    return normalizeReporteVentasPorUsuarioDetalle(raw);
  },
  presupuestosPorUsuario: async (
    params: ReportesRangoParams
  ): Promise<ReportePresupuestosPorUsuarioResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/presupuestos-por-usuario/", {
      params,
    });
    return normalizeReportePresupuestosPorUsuario(raw);
  },
  presupuestosPorUsuarioDetalle: async (
    params: ReportesRangoParams & { usuario_id: number | "sin_usuario" }
  ): Promise<ReportePresupuestosPorUsuarioDetalleResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/presupuestos-por-usuario/detalle/", {
      params: {
        ...params,
        usuario_id: params.usuario_id === "sin_usuario" ? "sin_usuario" : params.usuario_id,
      },
    });
    return normalizeReportePresupuestosPorUsuarioDetalle(raw);
  },
  /** Filtro mínimo de proveedor para informes. No es GET /api/proveedores/. */
  proveedoresCombo: async (params?: {
    activo?: boolean;
  }): Promise<{ id: number; codigo: string; nombre: string }[]> => {
    const raw = await apiRequest<{
      count?: number;
      results?: { id: number; codigo: string; nombre: string }[];
    }>("/reportes/proveedores-combo/", {
      params: {
        ...(params?.activo === false ? { activo: "false" } : { activo: "true" }),
      },
    });
    return raw.results ?? [];
  },
  sucursalesCombo: async (): Promise<{ id: number; codigo: string; nombre: string }[]> => {
    const raw = await apiRequest<{
      count?: number;
      results?: { id: number; codigo: string; nombre: string }[];
    }>("/reportes/sucursales-combo/");
    return raw.results ?? [];
  },
  stock: async (params: {
    vista?: "general" | "fabrica";
    /** Solo vista general: filtra productos por proveedor (Producto.proveedor_id). */
    proveedor_id?: number;
    producto_id?: number;
    /** Solo vista general: búsqueda por código/nombre/código proveedor. */
    producto_q?: string;
    sucursal_id?: number;
  }): Promise<ReporteStockResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/stock/", {
      params: {
        ...params,
        ...(params.sucursal_id != null ? { sucursal_id: params.sucursal_id } : {}),
      },
    });
    return normalizeReporteStock(raw);
  },
  precios: async (params?: {
    q?: string;
    grupo_id?: number;
    proveedor_id?: number;
    /** Default true en backend. */
    solo_activos?: boolean;
    /** Default false en backend. */
    incluir_personalizados?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<ReportePreciosResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/precios/", {
      params: {
        ...(params?.q ? { q: params.q } : {}),
        ...(params?.grupo_id != null ? { grupo_id: params.grupo_id } : {}),
        ...(params?.proveedor_id != null ? { proveedor_id: params.proveedor_id } : {}),
        ...(params?.solo_activos === false ? { solo_activos: "false" } : {}),
        ...(params?.incluir_personalizados ? { incluir_personalizados: "true" } : {}),
        ...(params?.page != null ? { page: params.page } : {}),
        ...(params?.page_size != null ? { page_size: params.page_size } : {}),
      },
    });
    return normalizeReportePrecios(raw);
  },
  /** Dump completo del catálogo filtrado — solo para CSV. La tabla usa `precios` paginado. */
  preciosExport: async (params?: {
    q?: string;
    grupo_id?: number;
    proveedor_id?: number;
    solo_activos?: boolean;
    incluir_personalizados?: boolean;
  }): Promise<ReportePreciosResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/precios-export/", {
      params: {
        ...(params?.q ? { q: params.q } : {}),
        ...(params?.grupo_id != null ? { grupo_id: params.grupo_id } : {}),
        ...(params?.proveedor_id != null ? { proveedor_id: params.proveedor_id } : {}),
        ...(params?.solo_activos === false ? { solo_activos: "false" } : {}),
        ...(params?.incluir_personalizados ? { incluir_personalizados: "true" } : {}),
      },
    });
    return normalizeReportePrecios(raw);
  },
  clientesDeudores: async (
    params: ReportesRangoParams & { modo?: "actividad" | "saldo_actual"; order?: "saldo" | "nombre" | "codigo" }
  ): Promise<ReporteClientesDeudoresResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/clientes-deudores/", { params });
    return normalizeReporteClientesDeudores(raw);
  },
  remitosAbiertos: async (params?: {
    sucursal_id?: number;
  }): Promise<ReporteRemitosAbiertosResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/remitos-abiertos/", {
      params: {
        ...(params?.sucursal_id != null ? { sucursal_id: params.sucursal_id } : {}),
      },
    });
    return normalizeReporteRemitosAbiertos(raw);
  },
  cheques: async (
    params: ReportesRangoParams & {
      tipo?: "todos" | "propio" | "terceros";
      estado_filtro?:
        | "todos"
        | "disponible"
        | "cobrado"
        | "cartera"
        | "pendiente"
        | "rechazado"
        | "anulado";
    }
  ): Promise<ReporteChequesResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/cheques/", { params });
    return normalizeReporteCheques(raw);
  },
  movimientosCaja: async (
    params: ReportesRangoParams & {
      tipo_movimiento_id?: number;
      usuario_id?: number | "sin_usuario";
    }
  ): Promise<ReporteMovimientosCajaResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/movimientos-caja/", {
      params: {
        ...params,
        usuario_id:
          params.usuario_id === "sin_usuario" ? "sin_usuario" : params.usuario_id,
      },
    });
    return normalizeReporteMovimientosCaja(raw);
  },
  valorizacionStock: async (params?: {
    sucursal_id?: number;
    deposito_id?: number;
  }): Promise<ReporteValorizacionStockResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/valorizacion-stock/", {
      params: {
        ...(params?.sucursal_id != null ? { sucursal_id: params.sucursal_id } : {}),
        ...(params?.deposito_id != null ? { deposito_id: params.deposito_id } : {}),
      },
    });
    return normalizeReporteValorizacionStock(raw);
  },
  dashboardPendientes: async (params?: { limit?: number }): Promise<DashboardPendientesResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/dashboard-pendientes/", { params });
    return normalizeDashboardPendientes(raw);
  },
  dashboardSerie: async (params: {
    meses: 1 | 3 | 6 | 12;
    yoy?: boolean;
  }): Promise<DashboardSerieResponse> => {
    const raw = await apiRequest<Record<string, unknown>>("/reportes/dashboard-serie/", {
      params: { meses: params.meses, yoy: params.yoy ? 1 : 0 },
    });
    return normalizeDashboardSerie(raw);
  },
  refreshDashboardSerie: async (): Promise<{ ok: boolean; mode?: string }> => {
    return apiRequest<{ ok: boolean; mode?: string }>("/reportes/dashboard-serie-refresh/", {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
};

// ============================================================================
// RECIBOS API
// ============================================================================

export interface ReciboPago {
  id?: number;
  reciboId?: number;
  fecha: string;
  metodoPago: string;
  monto: number;
  montoUsd?: number;
  cotizacion?: number;
  chequeId?: string;
  chequeTipo?: string;
  chequeNumero?: string;
  chequeBanco?: string;
  chequeFechaCreacion?: string;
  chequeFecha?: string;
  usarChequeExistente?: boolean;
}

export interface Recibo {
  id?: string;
  numeroRecibo: string;
  clienteCodigo: string;
  clienteNombre: string;
  fecha: string;
  totalRecibo: number;
  saldoAnterior: number;
  saldoNuevo: number;
  observaciones?: string;
  pdfUrl?: string;
  pagos: ReciboPago[];
  usuarioNombre?: string | null;
  usuario_nombre?: string | null;
  /** Importe del recibo en USD cuando se da vuelto en pesos (solo alta). */
  vueltoImporteUsd?: number | null;
  vueltoArs?: number;
}

export const recibosAPI = {
  list: async (params?: { search?: string; clienteId?: number }): Promise<Recibo[]> => {
    const response = await apiRequest<PaginatedResponse<Recibo>>('/recibos/', { params });
    return response.results;
  },

  listPaginated: async (params?: { search?: string; clienteId?: number } & PaginationParams): Promise<PaginatedResponse<Recibo>> => {
    return apiRequest<PaginatedResponse<Recibo>>('/recibos/', { params });
  },

  get: async (numeroRecibo: string): Promise<Recibo> => {
    return apiRequest<Recibo>(`/recibos/${numeroRecibo}/`);
  },

  fechasDisponibles: async (): Promise<{ fechas: string[]; default?: string | null }> => {
    return apiRequest<{ fechas: string[]; default?: string | null }>(
      '/recibos/fechas-disponibles/',
    );
  },

  create: async (data: Omit<Recibo, 'id' | 'numeroRecibo' | 'clienteCodigo' | 'clienteNombre'> & { cliente: number }): Promise<Recibo> => {
    const body: Record<string, unknown> = {
      cliente: data.cliente,
      fecha: data.fecha,
      totalRecibo: data.totalRecibo,
      saldoAnterior: data.saldoAnterior,
      saldoNuevo: data.saldoNuevo,
      pagos: data.pagos.map((p) => {
        const row: Record<string, unknown> = {
          fecha: p.fecha,
          metodoPago: p.metodoPago,
          monto: p.monto,
        };
        // USD físico: enviar monto en dólares + cotización para que el cierre los registre
        // como dólares físicos (no como pesos). Sin esto el ReciboPago queda con monto_usd=null.
        if (p.montoUsd != null) row.montoUsd = p.montoUsd;
        if (p.cotizacion != null) row.cotizacion = p.cotizacion;
        if (p.chequeId != null && p.chequeId !== '') row.chequeId = p.chequeId;
        if (p.chequeTipo != null && p.chequeTipo !== '') row.chequeTipo = p.chequeTipo;
        if (p.chequeNumero != null && p.chequeNumero !== '') row.chequeNumero = p.chequeNumero;
        if (p.chequeBanco != null && p.chequeBanco !== '') row.chequeBanco = p.chequeBanco;
        if (p.chequeFechaCreacion != null && p.chequeFechaCreacion !== '') row.chequeFechaCreacion = p.chequeFechaCreacion;
        if (p.chequeFecha != null && p.chequeFecha !== '') row.chequeFecha = p.chequeFecha;
        return row;
      }),
    };
    if (data.observaciones != null && data.observaciones !== '') {
      body.observaciones = data.observaciones;
    }
    if (data.vueltoImporteUsd != null) {
      body.vueltoImporteUsd = data.vueltoImporteUsd;
    }

    return apiRequest<Recibo>('/recibos/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  update: async (numeroRecibo: string, data: Partial<Recibo>): Promise<Recibo> => {
    const body: Record<string, unknown> = {};
    if (data.totalRecibo !== undefined) body.totalRecibo = data.totalRecibo;
    if (data.saldoAnterior !== undefined) body.saldoAnterior = data.saldoAnterior;
    if (data.saldoNuevo !== undefined) body.saldoNuevo = data.saldoNuevo;
    if (data.observaciones !== undefined) body.observaciones = data.observaciones;
    if (data.pdfUrl !== undefined) body.pdfUrl = data.pdfUrl;
    if (data.pagos !== undefined) {
      body.pagos = data.pagos.map((p) => {
        const row: Record<string, unknown> = {
          fecha: p.fecha,
          metodoPago: p.metodoPago,
          monto: p.monto,
        };
        if (p.id != null) row.id = p.id;
        // USD físico: ver nota en recibosAPI.create.
        if (p.montoUsd != null) row.montoUsd = p.montoUsd;
        if (p.cotizacion != null) row.cotizacion = p.cotizacion;
        if (p.chequeId != null && p.chequeId !== '') row.chequeId = p.chequeId;
        if (p.chequeTipo != null && p.chequeTipo !== '') row.chequeTipo = p.chequeTipo;
        if (p.chequeNumero != null && p.chequeNumero !== '') row.chequeNumero = p.chequeNumero;
        if (p.chequeBanco != null && p.chequeBanco !== '') row.chequeBanco = p.chequeBanco;
        if (p.chequeFechaCreacion != null && p.chequeFechaCreacion !== '') row.chequeFechaCreacion = p.chequeFechaCreacion;
        if (p.chequeFecha != null && p.chequeFecha !== '') row.chequeFecha = p.chequeFecha;
        return row;
      });
    }

    return apiRequest<Recibo>(`/recibos/${numeroRecibo}/`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  delete: async (numeroRecibo: string): Promise<void> => {
    return apiRequest<void>(`/recibos/${numeroRecibo}/`, {
      method: 'DELETE',
    });
  },

  /** PDF generado en backend con plantilla pdfme `pdf_template_recibo` (SystemConfig). */
  downloadReciboPDF: async (numeroRecibo: string): Promise<File> => {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE_URL}/recibos/${encodeURIComponent(numeroRecibo)}/pdf/`, {
      method: 'GET',
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error(`Recibo PDF download failed: ${response.status}`);
    }
    return pdfFileFromResponse(response, "rc_comprobante.pdf");
  },
};

// ============================================================================
// VENTA REMITOS API
// ============================================================================

export interface VentaRemitoDepositoAllocacion {
  id: number;
  cantidad: number;
}

export interface VentaRemitoEntregaItem {
  id: number;
  cantidad_entregada: number;
  entregado: boolean;
  depositoId?: number;
  depositoIds?: number[];
  depositos?: VentaRemitoDepositoAllocacion[];
}

export interface VentaRemitoDepositoOpcion {
  id: number;
  nombre: string;
  cantidad: string | number;
  stock?: string | number;
  puedeCubrirSolo?: boolean;
}

export interface VentaRemitoItemOpcionesDeposito {
  id: number;
  productoId?: number;
  nombreProducto?: string;
  codigoProducto?: string;
  cantidad?: string | number;
  depositos: VentaRemitoDepositoOpcion[];
  puedeCubrirSolo?: boolean;
  sumaCubre?: boolean;
  comboGroup?: string | null;
  combo_group?: string | null;
}

export interface VentaRemitoOpcionesDeposito {
  sucursalId?: number;
  sucursalNombre?: string;
  items: VentaRemitoItemOpcionesDeposito[];
}

export interface VentaRemitoDetalle {
  id?: number;
  remitoId?: number;
  productoId?: number;
  codigoProducto?: string;
  nombreProducto?: string;
  cantidadFacturada: number;
  cantidadEntregada: number;
  entregado: boolean;
  stockEstado?: "disponible" | "pedido" | "sin_stock";
  stock_estado?: "disponible" | "pedido" | "sin_stock";
  /** F5: copia del group id. F6 agrupa visualmente en la grilla. */
  comboGroup?: string | null;
  combo_group?: string | null;
  /** P8-2: tras NC, Cant. clampada a lo entregado en remito parcial. */
  cantidadBloqueada?: boolean;
  cantidad_bloqueada?: boolean;
}

export interface VentaRemitoPdfSnapshot {
  url: string;
  estado: string;
  at?: string;
  /** PDF compartido de varios remitos del mismo cliente. */
  grupo?: boolean;
}

export interface VentaRemito {
  id?: number;
  numero: string;
  facturaId?: number;
  facturaNumero?: string;
  numeroFactura?: string | null;
  clienteId?: number;
  clienteNombre?: string;
  puntoVentaId?: number | null;
  punto_venta?: number | null;
  fecha: string;
  estado: 'pendiente' | 'parcial' | 'entregado' | 'cancelado';
  modo_entrega?: 'domicilio' | 'presencial';
  modoEntrega?: 'domicilio' | 'presencial'; // Alias for CamelCase API response
  /** Factura finalizada en CC → tag «deudor» (#15) */
  esDeudor?: boolean;
  es_deudor?: boolean;
  /** F7: alta elevated sin factura. */
  sinFactura?: boolean;
  sin_factura?: boolean;
  total?: number | null;
  observaciones?: string;
  /** Historial de PDFs generados (parcial / final); para descarga versionada */
  pdfSnapshots?: VentaRemitoPdfSnapshot[];
  pdf_snapshots?: VentaRemitoPdfSnapshot[];
  pdfUrl?: string | null;
  pdf_url?: string | null;
  items: VentaRemitoDetalle[];
  usuarioNombre?: string | null;
  usuario_nombre?: string | null;
  createdBy?: number | null;
  created_by?: number | null;
  finalizadoUsuarioNombre?: string | null;
  finalizado_usuario_nombre?: string | null;
  entregaLista?: boolean;
  entrega_lista?: boolean;
  avisoIngreso?: boolean;
  aviso_ingreso?: boolean;
}

function ventaRemitoQueryParams(
  params?: {
    estado?: string;
    clienteId?: number;
    facturaId?: number;
    search?: string;
    /** Punto 8: listado «míos» (created_by). El módulo manda 1/0 explícito. */
    mios?: boolean | "1" | "0";
    /** Switch extra: solo remitos pendiente/parcial. Omitido = no filtra. */
    conPendiente?: boolean | "1" | "0";
    con_pendiente?: boolean | "1" | "0";
  } & PaginationParams,
) {
  if (!params) return undefined;
  const { clienteId, facturaId, mios, conPendiente, con_pendiente, ...rest } = params;
  let miosParam: "1" | "0" | undefined;
  if (mios === true || mios === "1") miosParam = "1";
  else if (mios === false || mios === "0") miosParam = "0";
  const rawPend = conPendiente ?? con_pendiente;
  let conPendienteParam: "1" | undefined;
  if (rawPend === true || rawPend === "1") conPendienteParam = "1";
  return {
    ...rest,
    ...(clienteId != null ? { cliente: clienteId } : {}),
    ...(facturaId != null ? { factura: facturaId } : {}),
    ...(miosParam != null ? { mios: miosParam } : {}),
    ...(conPendienteParam != null ? { con_pendiente: conPendienteParam } : {}),
  };
}

export const ventaRemitosAPI = {
  list: async (params?: {
    estado?: string;
    clienteId?: number;
    facturaId?: number;
    mios?: boolean | "1" | "0";
  }): Promise<VentaRemito[]> => {
    const response = await apiRequest<PaginatedResponse<VentaRemito>>('/venta-remitos/', {
      params: ventaRemitoQueryParams(params),
    });
    return response.results;
  },

  listPaginated: async (params?: {
    estado?: string;
    clienteId?: number;
    facturaId?: number;
    search?: string;
    mios?: boolean | "1" | "0";
    conPendiente?: boolean | "1" | "0";
    con_pendiente?: boolean | "1" | "0";
  } & PaginationParams): Promise<PaginatedResponse<VentaRemito>> => {
    return apiRequest<PaginatedResponse<VentaRemito>>('/venta-remitos/', {
      params: ventaRemitoQueryParams(params),
    });
  },

  /** Todos los remitos de un cliente (PV del usuario, sin clamp de mes). */
  listAllForCliente: async (
    clienteId: number,
    extra?: { mios?: boolean | "1" | "0" },
  ): Promise<VentaRemito[]> => {
    const out: VentaRemito[] = [];
    let page = 1;
    const page_size = 200;
    for (;;) {
      const response = await ventaRemitosAPI.listPaginated({
        clienteId,
        page,
        page_size,
        ...(extra?.mios != null ? { mios: extra.mios } : {}),
      });
      const batch = response.results ?? [];
      out.push(...batch);
      if (!response.next || batch.length === 0) break;
      page += 1;
      if (page > 200) break;
    }
    return out;
  },

  get: async (numero: string): Promise<VentaRemito> => {
    return apiRequest<VentaRemito>(`/venta-remitos/${numero}/`);
  },

  marcarAvisoIngresoVisto: async (clienteId: number): Promise<{ ok: boolean; updated: number }> => {
    return apiRequest('/venta-remitos/aviso-ingreso-visto/', {
      method: 'POST',
      body: JSON.stringify({ clienteId }),
    });
  },

  create: async (data: {
    cliente: number;
    punto_venta: number;
    fecha?: string;
    estado?: VentaRemito["estado"];
    modo_entrega?: VentaRemito["modo_entrega"];
    items: Array<{
      producto: number;
      cantidadFacturada: number;
      cantidadEntregada?: number;
      entregado?: boolean;
      comboGroup?: string | null;
    }>;
  }): Promise<VentaRemito> => {
    return apiRequest<VentaRemito>('/venta-remitos/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (numero: string, data: Partial<VentaRemito>): Promise<VentaRemito> => {
    return apiRequest<VentaRemito>(`/venta-remitos/${numero}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /** Nombre impreso de una línea. No cambia el artículo ni la factura. */
  updateItemNombre: async (
    numero: string,
    itemId: number,
    nombreProducto: string,
  ): Promise<VentaRemito> => {
    return apiRequest<VentaRemito>(`/venta-remitos/${numero}/update-item-nombre/`, {
      method: 'PATCH',
      body: JSON.stringify({ itemId, nombreProducto }),
    });
  },

  delete: async (numero: string): Promise<void> => {
    return apiRequest<void>(`/venta-remitos/${numero}/`, {
      method: 'DELETE',
    });
  },

  marcarEntregadoGrupo: async (
    remitos: { numero: string; items: VentaRemitoEntregaItem[] }[],
    password?: string,
  ): Promise<{ remitos: VentaRemito[] }> => {
    const body: Record<string, unknown> = { remitos };
    const pwd = (password || "").trim();
    if (pwd) body.password = pwd;
    return apiRequest<{ remitos: VentaRemito[] }>(
      "/venta-remitos/marcar_entregado_grupo/",
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  marcarEntregado: async (
    numero: string,
    items?: VentaRemitoEntregaItem[],
    depositoId?: number,
    password?: string,
  ): Promise<VentaRemito> => {
    const body = remitoEntregaBody({
      items: items || [],
      depositoId,
      password,
    });
    return apiRequest<VentaRemito>(`/venta-remitos/${numero}/marcar_entregado/`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  marcarParcial: async (
    numero: string,
    items?: VentaRemitoEntregaItem[],
    depositoId?: number,
    password?: string,
  ): Promise<VentaRemito> => {
    const body = remitoEntregaBody({
      items: items || [],
      depositoId,
      password,
    });
    return apiRequest<VentaRemito>(`/venta-remitos/${numero}/marcar_parcial/`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  reabrir: async (numero: string): Promise<VentaRemito> => {
    return apiRequest<VentaRemito>(`/venta-remitos/${numero}/reabrir/`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  opcionesDeposito: async (
    numero: string,
    items?: VentaRemitoEntregaItem[],
  ): Promise<VentaRemitoOpcionesDeposito> => {
    return apiRequest<VentaRemitoOpcionesDeposito>(
      `/venta-remitos/${numero}/opciones_deposito/`,
      {
        method: 'POST',
        body: JSON.stringify({ items: items || [] }),
      },
    );
  },

  cambiarItem: async (
    numero: string,
    itemId: number,
    productoId: number,
  ): Promise<VentaRemito> => {
    return apiRequest<VentaRemito>(`/venta-remitos/${numero}/cambiar_item/`, {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId, producto_id: productoId }),
    });
  },

  updateItem: async (numero: string, itemId: number, data: Partial<VentaRemitoDetalle>): Promise<VentaRemito> => {
    return apiRequest<VentaRemito>(`/venta-remitos/${numero}/`, {
      method: 'PATCH',
      body: JSON.stringify({ items: [{ id: itemId, ...data }] }),
    });
  },

  downloadRemitoPDF: async (numero: string, version: string = 'latest'): Promise<File> => {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const qs = version
      ? `?version=${encodeURIComponent(version)}`
      : '';
    const response = await fetch(`${API_BASE_URL}/venta-remitos/${encodeURIComponent(numero)}/pdf${qs}`, {
      method: 'GET',
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error(`Remito PDF download failed: ${response.status}`);
    }
    return pdfFileFromResponse(response, "rv_comprobante.pdf");
  },
};

// ============================================================================
// CHEQUES API
// ============================================================================

export interface Cheque {
  id?: number;
  codigo: string;
  numeroCheque: string;
  nroCheque?: string; // Alias for backwards compatibility
  banco: string;
  fechaEmision: string;
  fechaCobro?: string;
  emisor?: string;
  emisorNombre?: string; // Alias for display
  destino?: string;
  origen: 'cliente' | 'proveedor' | 'otro';
  origenId?: number;
  tipo: 'propio' | 'terceros';
  /** Medio: API puede devolver `fisico` | `electronico` (BD) o etiquetas legadas. */
  tipoCheque?: 'Físico' | 'Electrónico' | 'fisico' | 'electronico' | string;
  importe: number;
  moneda: 'ARS' | 'USD';
  cobrado: boolean;
  fechaCobrado?: string;
  estado?: 'pendiente' | 'depositado' | 'rechazado' | 'cobrado' | 'disponible' | 'cartera' | 'anulado';
  observaciones?: string;
  impuestoRechazo?: number | string;
  impuesto_rechazo?: number | string;
}

export const chequesAPI = {
  list: async (params?: { search?: string; cobrado?: boolean; estado?: string }): Promise<Cheque[]> => {
    const response = await apiRequest<PaginatedResponse<Cheque> | Cheque[]>('/cheques/', { params });
    return Array.isArray(response) ? response : response.results;
  },

  listPaginated: async (
    params?: {
      search?: string;
      cobrado?: boolean;
      estado?: string;
      /** propio | terceros | todos (omitido = todos) */
      tipo?: 'todos' | 'propio' | 'terceros';
      /** Misma semántica que reportes/cheques estado_filtro */
      estado_filtro?:
        | 'todos'
        | 'disponible'
        | 'cobrado'
        | 'cartera'
        | 'pendiente'
        | 'rechazado'
        | 'anulado';
      /** Inclusive YYYY-MM-DD; campo según `fecha_tipo` (default emision). */
      fecha_desde?: string;
      fecha_hasta?: string;
      /** `emision` | `carga` | `cobro` — solo valores admitidos en backend */
      fecha_tipo?: 'emision' | 'carga' | 'cobro';
      /** @deprecated Preferir `fecha_desde`/`fecha_hasta`; implica tipo emision */
      fecha_emision_desde?: string;
      fecha_emision_hasta?: string;
    } & PaginationParams,
  ): Promise<PaginatedResponse<Cheque>> => {
    return apiRequest<PaginatedResponse<Cheque>>('/cheques/', { params });
  },

  get: async (codigo: string): Promise<Cheque> => {
    return apiRequest<Cheque>(`/cheques/${codigo}/`);
  },

  create: async (data: Omit<Cheque, 'id'>): Promise<Cheque> => {
    return apiRequest<Cheque>('/cheques/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (codigo: string, data: Partial<Cheque>): Promise<Cheque> => {
    return apiRequest<Cheque>(`/cheques/${codigo}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  marcarCobrado: async (codigo: string): Promise<Cheque> => {
    return apiRequest<Cheque>(`/cheques/${codigo}/cobrar/`, {
      method: 'POST',
    });
  },

  /** Punto 5: solo cheques de cliente. Debita saldo = importe + impuesto. */
  rechazar: async (id: number, data?: { impuesto?: number }): Promise<Cheque> => {
    return apiRequest<Cheque>(`/cheques/${id}/rechazar/`, {
      method: 'POST',
      body: JSON.stringify({ impuesto: data?.impuesto ?? 0 }),
    });
  },

  getNextCodigo: async (): Promise<{ codigo: string }> => {
    return apiRequest<{ codigo: string }>('/cheques/next-codigo/');
  },

  getDisponibles: async (): Promise<Cheque[]> => {
    return apiRequest<Cheque[]>('/cheques/disponibles/');
  },
};

// ============================================================================
// CAJA Y BANCOS API
// ============================================================================

export interface Banco {
  id?: number;
  codigo: string;
  nombreBanco: string;
  tipoCuenta: string;
  numeroCuenta?: string;
  cbu?: string;
  alias?: string;
  saldoInicial: number;
  saldoActual: number;
  moneda: 'ARS' | 'USD';
  fechaCreacion: string;
  activo: boolean;
  observaciones?: string;
}

export interface TipoMovimiento {
  id?: number;
  codigo: string;
  nombre: string;
  descripcion?: string;
  activo?: boolean;
  fondo?: boolean;
  tipoFondo?: 'Ingreso' | 'Egreso';
  tipo?: 'Ingreso' | 'Egreso';
  moneda?: 'ARS' | 'USD';
  linkCierreDiario?: boolean;
  asociarUsuario?: boolean;
}

export interface MovimientoCaja {
  id?: number;
  codigo: string;
  fecha: string;
  tipoMovimiento?: number;  // FK ID (camelCase from API)
  tipoMovimientoNombre?: string;  // Serializer read-only field (camelCase from API)
  tipo_movimiento?: number;  // Legacy snake_case alias
  tipo_movimiento_nombre?: string;  // Legacy snake_case alias
  importe: number;
  usuario?: number;
  usuarioNombre?: string;     // From serializer (camelCase from API)
  usuario_nombre?: string;    // Legacy snake_case alias
  usuarioCodigo?: string;
  usuario_codigo?: string;    // Legacy snake_case alias
  metodoPago?: string;
  metodo_pago?: string;       // Legacy snake_case alias
  observaciones?: string;
  createdAt?: string;
  /** Código del Fondo vivo (fondo_generado). Null/ausente = huérfano o sin fondos. */
  fondoCodigo?: string | null;
  fondo_codigo?: string | null;
}

export interface CierreDiario {
  id?: number;
  fecha: string;
  sucursal?: number;
  sucursalId?: number;
  sucursal_id?: number;
  sucursalNombre?: string;
  sucursal_nombre?: string;
  esCasaCentral?: boolean;
  es_casa_central?: boolean;
  usuarioCierreId?: number;
  efectivoIngresos: number;
  tarjetaCreditoIngresos: number;
  tarjetaDebitoIngresos: number;
  transferenciaIngresos: number;
  chequesIngresos: number;
  dolaresIngresos: number;
  dolares_ingresos?: number;
  dolaresGastos?: number;
  dolares_gastos?: number;
  efectivoGastos: number;
  tarjetaCreditoGastos: number;
  tarjetaDebitoGastos: number;
  transferenciaGastos: number;
  chequesGastos: number;
  totalIngresos: number;
  totalGastos: number;
  totalNeto: number;
  cantidadFacturasCae: number;
  cantidadFacturasFx: number;
  totalFacturas: number;
  total_facturas?: number; // Alias
  saldoInicial?: number;
  saldo_inicial?: number; // Alias
  saldoInicialUsd?: number;
  saldo_inicial_usd?: number;
  efectivo_ingresos?: number;
  efectivo_gastos?: number;
  cheques_ingresos?: number;
  cheques_gastos?: number;
  tarjeta_credito_ingresos?: number;
  tarjeta_debito_ingresos?: number;
  transferencia_ingresos?: number;
  tarjeta_credito_gastos?: number;
  tarjeta_debito_gastos?: number;
  transferencia_gastos?: number;
  total_ingresos?: number;
  total_gastos?: number;
  total_neto?: number;
  saldoActual?: number;
  observaciones?: string;
  cerrado: boolean;
  fechaCierre?: string;
  detalles: CierreDiarioDetalle[];
}

export interface CierreDiarioDetalle {
  id?: number;
  cierreId?: number;
  cierre?: number;
  origenKey?: string;
  origen_key?: string;
  tipo: string;
  referencia?: string;
  descripcion?: string;
  metodoPago?: string;
  metodo_pago?: string;
  esIngreso: boolean;
  es_ingreso?: boolean;
  importe: number;
  montoUsd?: number;
  monto_usd?: number;
  cotizacion?: number;
  importeArs?: number;
  importe_ars?: number;
  monedaLinea?: 'ARS' | 'USD';
  moneda_linea?: 'ARS' | 'USD';
  /** F4: pk de factura asociada (origen factura / factura_pago); null si no aplica. */
  facturaId?: number | null;
  factura_id?: number | null;
  /** F3: quién generó factura/movimiento/fondo (created_by; null → «Sin usuario»). */
  usuarioNombre?: string | null;
  usuario_nombre?: string | null;
  /** F5: el tipo de movimiento tiene «Asociar a usuario». */
  asociarUsuario?: boolean;
  asociar_usuario?: boolean;
  /** F5: destinatario (MovimientoCaja.usuario). Null si el tipo no asocia. */
  destinatarioNombre?: string | null;
  destinatario_nombre?: string | null;
  usuarioDestinoId?: number | null;
  usuario_destino_id?: number | null;
  /** Legacy: true solo si verificacionEstado === 'ok'. */
  verificado: boolean;
  /** '' | 'ok' | 'rechazado' — ciclo pendiente → ok → rechazado → pendiente */
  verificacionEstado?: '' | 'ok' | 'rechazado';
  verificacion_estado?: '' | 'ok' | 'rechazado';
  observacion: string;
  verificadoPor?: number | null;
  verificadoPorUsername?: string | null;
  verificadoEn?: string | null;
  updatedAt: string;
  updated_at?: string;
}

export interface FacturaPago {
  id?: number;
  facturaId?: number;
  metodoPago: string;
  importeArs: number;
  montoUsd?: number;
  cotizacion?: number;
  orden?: number;
}

export const cajaBancosAPI = {
  // Bancos
  listBancos: async (): Promise<Banco[]> => {
    const response = await apiRequest<PaginatedResponse<Banco> | Banco[]>('/bancos/');
    return Array.isArray(response) ? response : response.results;
  },

  createBanco: async (data: Omit<Banco, 'id' | 'saldoActual'>): Promise<Banco> => {
    return apiRequest<Banco>('/bancos/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateBanco: async (codigo: string, data: Partial<Banco>): Promise<Banco> => {
    return apiRequest<Banco>(`/bancos/${codigo}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteBanco: async (codigo: string): Promise<void> => {
    return apiRequest<void>(`/bancos/${codigo}/`, {
      method: 'DELETE',
    });
  },

  // Tipos de Movimiento
  listTiposMovimiento: async (): Promise<TipoMovimiento[]> => {
    const response = await apiRequest<PaginatedResponse<TipoMovimiento> | TipoMovimiento[]>('/tipos-movimiento/');
    return Array.isArray(response) ? response : response.results;
  },

  createTipoMovimiento: async (data: Omit<TipoMovimiento, 'id'>): Promise<TipoMovimiento> => {
    return apiRequest<TipoMovimiento>('/tipos-movimiento/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateTipoMovimiento: async (codigo: string, data: Partial<TipoMovimiento>): Promise<TipoMovimiento> => {
    return apiRequest<TipoMovimiento>(`/tipos-movimiento/${encodeURIComponent(codigo)}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteTipoMovimiento: async (codigo: string): Promise<void> => {
    return apiRequest<void>(`/tipos-movimiento/${encodeURIComponent(codigo)}/`, {
      method: 'DELETE',
    });
  },

  // Movimientos
  listMovimientos: async (params?: {
    fechaDesde?: string;
    fechaHasta?: string;
    page_size?: number;
    page?: number;
    historico?: number | boolean;
  }): Promise<MovimientoCaja[]> => {
    const r = await apiRequest<PaginatedResponse<MovimientoCaja> | MovimientoCaja[]>('/movimientos-caja/', { params });
    return Array.isArray(r) ? r : (r.results ?? []);
  },

  createMovimiento: async (data: Omit<MovimientoCaja, 'id' | 'codigo'>): Promise<MovimientoCaja> => {
    return apiRequest<MovimientoCaja>('/movimientos-caja/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  deleteMovimiento: async (id: number): Promise<void> => {
    return apiRequest<void>(`/movimientos-caja/${id}/`, {
      method: 'DELETE',
    });
  },

  // Cierres Diarios
  listCierres: async (params?: {
    fechaDesde?: string;
    fechaHasta?: string;
    sucursal?: number;
    page_size?: number;
    page?: number;
    historico?: number | boolean;
  }): Promise<CierreDiario[]> => {
    const r = await apiRequest<PaginatedResponse<CierreDiario> | CierreDiario[]>('/cierres-diarios/', { params });
    return Array.isArray(r) ? r : (r.results ?? []);
  },

  getCierre: async (id: number | string): Promise<CierreDiario> => {
    return apiRequest<CierreDiario>(`/cierres-diarios/${id}/`);
  },

  generarCierre: async (data?: {
    saldo_inicial?: number;
    sucursal?: number;
    /** Fecha del cierre. Default BE: última caja de la sucursal + 1 (no hoy). */
    fecha?: string;
  }): Promise<CierreDiario> => {
    return apiRequest<CierreDiario>('/cierres-diarios/generar/', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  },

  cerrarCierre: async (id: number | string, password?: string): Promise<CierreDiario> => {
    return apiRequest<CierreDiario>(`/cierres-diarios/${id}/cerrar/`, {
      method: 'POST',
      body: JSON.stringify(cierreFinalizarBody(password)),
    });
  },

  cambiarMetodoVenta: async (
    cierreId: number | string,
    data: {
      origenKey: string;
      metodoPago: string;
      montoUsd?: string;
      cotizacion?: string;
    },
  ): Promise<CierreDiario> => {
    return apiRequest<CierreDiario>(`/cierres-diarios/${cierreId}/metodo-venta/`, {
      method: "POST",
      body: JSON.stringify({
        origenKey: data.origenKey,
        metodoPago: data.metodoPago,
        ...(data.montoUsd != null ? { montoUsd: data.montoUsd } : {}),
        ...(data.cotizacion != null ? { cotizacion: data.cotizacion } : {}),
      }),
    });
  },

  updateCierreDetalle: async (
    cierreId: number | string,
    detalleId: number | string,
    data: {
      verificacionEstado?: '' | 'ok' | 'rechazado';
      verificado?: boolean;
      observacion?: string;
      updatedAt: string;
    }
  ): Promise<CierreDiarioDetalle> => {
    return apiRequest<CierreDiarioDetalle>(
      `/cierres-diarios/${cierreId}/detalles/${detalleId}/`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
  },
};

// ============================================================================
// FONDOS API
// ============================================================================

export interface Fondo {
  id?: number;
  codigo: string;
  fecha: string;
  tipo: 'Ingreso' | 'Egreso';
  importe: number;
  moneda: 'ARS' | 'USD';
  concepto: string;
  origen?: 'caja' | 'banco' | 'cliente' | 'proveedores' | 'gasto' | 'otros' | null;  // Where money comes FROM
  destino?: 'caja' | 'banco' | 'cliente' | 'proveedores' | 'gasto' | 'otros' | null;  // Where money goes TO
  observaciones?: string;
  /** PK del MovimientoCaja si es auto-fondo (registrar_en_fondos). */
  movimiento_caja?: number | null;
}

export const fondosAPI = {
  list: async (params?: { tipo?: string; moneda?: string; fechaDesde?: string; fechaHasta?: string }): Promise<Fondo[]> => {
    const response = await apiRequest<PaginatedResponse<Fondo>>('/fondos/', { params });
    return response.results;
  },

  get: async (codigo: string): Promise<Fondo> => {
    return apiRequest<Fondo>(`/fondos/${codigo}/`);
  },

  create: async (data: Omit<Fondo, 'id' | 'codigo'>): Promise<Fondo> => {
    return apiRequest<Fondo>('/fondos/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (codigo: string, data: Partial<Fondo>): Promise<Fondo> => {
    return apiRequest<Fondo>(`/fondos/${codigo}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete: async (codigo: string): Promise<void> => {
    return apiRequest<void>(`/fondos/${codigo}/`, {
      method: 'DELETE',
    });
  },

  getBalance: async (): Promise<{ ARS: number; USD: number }> => {
    return apiRequest<{ ARS: number; USD: number }>('/fondos/balance/');
  },
};

// ============================================================================
// OPCIONES API (Settings/Configuration)
// ============================================================================

export interface Sucursal {
  id?: number;
  codigo: string;
  nombre: string;
  direccion?: string;
  ciudad?: string;
  provincia?: string;
  pais?: string;
  telefono?: string;
  email?: string;
  activo: boolean;
  esCasaCentral?: boolean;
  es_casa_central?: boolean;
  /** IDs de depósitos asociados (M2M inverso de Deposito.sucursales). */
  depositos?: number[];
  depositosNombres?: string[];
  depositos_nombres?: string[];
}

/** IDs de depósitos asociados a la sucursal (M2M). */
export function sucursalDepositoIds(s: Pick<Sucursal, "depositos">): number[] {
  if (Array.isArray(s.depositos)) {
    return s.depositos.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  }
  return [];
}

export interface Deposito {
  id?: number;
  codigo: string;
  nombre: string;
  /** @deprecated F5: usar sucursales (M2M). */
  sucursal?: number;
  sucursalNombre?: string;
  sucursales?: number[];
  sucursalesNombres?: string[];
  direccion?: string;
  activo: boolean;
}

/** IDs de sucursales asociadas al depósito (M2M; fallback al FK legado). */
export function depositoSucursalIds(d: Pick<Deposito, "sucursal" | "sucursales">): number[] {
  if (Array.isArray(d.sucursales) && d.sucursales.length > 0) {
    return d.sucursales.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  }
  if (d.sucursal != null && d.sucursal !== ("" as unknown as number)) {
    const n = Number(d.sucursal);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }
  return [];
}

export function depositoSirveSucursal(
  d: Pick<Deposito, "sucursal" | "sucursales">,
  sucursalId: number,
): boolean {
  return depositoSucursalIds(d).includes(Number(sucursalId));
}

export function depositoSucursalesLabel(d: Deposito): string {
  if (d.sucursalesNombres && d.sucursalesNombres.length > 0) {
    return d.sucursalesNombres.join(", ");
  }
  if (d.sucursalNombre) return d.sucursalNombre;
  return "-";
}

export interface MetodoPago {
  id?: number;
  codigo: string;
  tipo: string;
  interes: number;
  cuotasMax: number;
  activo: boolean;
}



export const opcionesAPI = {
  // Sucursales
  // Sucursales
  listSucursales: async (): Promise<Sucursal[]> => {
    const response = await apiRequest<PaginatedResponse<Sucursal> | Sucursal[]>('/sucursales/');
    // Handle both paginated and non-paginated responses
    return Array.isArray(response) ? response : response.results;
  },

  createSucursal: async (data: Omit<Sucursal, 'id'>): Promise<Sucursal> => {
    return apiRequest<Sucursal>('/sucursales/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateSucursal: async (codigo: string, data: Partial<Sucursal>): Promise<Sucursal> => {
    return apiRequest<Sucursal>(`/sucursales/${codigo}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteSucursal: async (codigo: string): Promise<void> => {
    return apiRequest<void>(`/sucursales/${codigo}/`, {
      method: 'DELETE',
    });
  },

  // Depósitos
  listDepositos: async (sucursalId?: number): Promise<Deposito[]> => {
    const response = await apiRequest<PaginatedResponse<Deposito> | Deposito[]>('/depositos/', {
      params: sucursalId ? { sucursal: sucursalId } : undefined,
    });
    // Handle both paginated and non-paginated responses
    return Array.isArray(response) ? response : response.results;
  },

  createDeposito: async (data: Omit<Deposito, 'id'>): Promise<Deposito> => {
    return apiRequest<Deposito>('/depositos/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateDeposito: async (codigo: string, data: Partial<Deposito>): Promise<Deposito> => {
    return apiRequest<Deposito>(`/depositos/${codigo}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteDeposito: async (codigo: string): Promise<void> => {
    return apiRequest<void>(`/depositos/${codigo}/`, {
      method: 'DELETE',
    });
  },

  // Puntos de Venta
  listPuntosVenta: async (sucursalId?: number): Promise<PuntoVenta[]> => {
    const response = await apiRequest<PaginatedResponse<PuntoVenta> | PuntoVenta[]>('/puntos-venta/', {
      params: sucursalId ? { sucursal: sucursalId } : undefined,
    });
    return Array.isArray(response) ? response : response.results;
  },

  createPuntoVenta: async (data: Omit<PuntoVenta, 'id'>): Promise<PuntoVenta> => {
    return apiRequest<PuntoVenta>('/puntos-venta/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updatePuntoVenta: async (codigo: string, data: Partial<PuntoVenta>): Promise<PuntoVenta> => {
    return apiRequest<PuntoVenta>(`/puntos-venta/${codigo}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deletePuntoVenta: async (codigo: string): Promise<void> => {
    return apiRequest<void>(`/puntos-venta/${codigo}/`, {
      method: 'DELETE',
    });
  },

  // Métodos de Pago
  listMetodosPago: async (): Promise<MetodoPago[]> => {
    const response = await apiRequest<PaginatedResponse<MetodoPago> | MetodoPago[]>('/metodos-pago/');
    return Array.isArray(response) ? response : response.results;
  },

  createMetodoPago: async (data: Omit<MetodoPago, 'id'>): Promise<MetodoPago> => {
    return apiRequest<MetodoPago>('/metodos-pago/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateMetodoPago: async (codigo: string, data: Partial<MetodoPago>): Promise<MetodoPago> => {
    return apiRequest<MetodoPago>(`/metodos-pago/${codigo}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteMetodoPago: async (codigo: string): Promise<void> => {
    return apiRequest<void>(`/metodos-pago/${codigo}/`, {
      method: 'DELETE',
    });
  },

  // Usuarios
  listUsuarios: async (): Promise<Usuario[]> => {
    // TODO: Uncomment when backend is ready
    // return apiRequest<Usuario[]>('/opciones/usuarios/');
    throw new Error('API not implemented yet');
  },

  createUsuario: async (_data: Omit<Usuario, 'id' | 'codigo'>): Promise<Usuario> => {
    // TODO: Uncomment when backend is ready
    // return apiRequest<Usuario>('/opciones/usuarios/', {
    //   method: 'POST',
    //   body: JSON.stringify(data),
    // });
    throw new Error('API not implemented yet');
  },

  updateUsuario: async (_codigo: string, _data: Partial<Usuario>): Promise<Usuario> => {
    // TODO: Uncomment when backend is ready
    // return apiRequest<Usuario>(`/opciones/usuarios/${codigo}/`, {
    //   method: 'PATCH',
    //   body: JSON.stringify(data),
    // });
    throw new Error('API not implemented yet');
  },

  deleteUsuario: async (_codigo: string): Promise<void> => {
    // TODO: Uncomment when backend is ready
    // return apiRequest<void>(`/opciones/usuarios/${codigo}/`, {
    //   method: 'DELETE',
    // });
    throw new Error('API not implemented yet');
  },

  // Roles
  listRoles: async (): Promise<Rol[]> => {
    // TODO: Uncomment when backend is ready
    // return apiRequest<Rol[]>('/opciones/roles/');
    throw new Error('API not implemented yet');
  },

  createRol: async (_data: Omit<Rol, 'id'>): Promise<Rol> => {
    // TODO: Uncomment when backend is ready
    // return apiRequest<Rol>('/opciones/roles/', {
    //   method: 'POST',
    //   body: JSON.stringify(data),
    // });
    throw new Error('API not implemented yet');
  },

  updateRol: async (_codigo: string, _data: Partial<Rol>): Promise<Rol> => {
    // TODO: Uncomment when backend is ready
    // return apiRequest<Rol>(`/opciones/roles/${codigo}/`, {
    //   method: 'PATCH',
    //   body: JSON.stringify(data),
    // });
    throw new Error('API not implemented yet');
  },
};

// ============================================================================
// EXCEL IMPORT API
// ============================================================================

export interface ExcelImportResult {
  success: boolean;
  imported: number;
  errors: Array<{
    row: number;
    field: string;
    message: string;
  }>;
}

export const excelAPI = {
  importStock: async (_file: File): Promise<ExcelImportResult> => {
    // TODO: Uncomment when backend is ready
    // const formData = new FormData();
    // formData.append('file', file);
    // return apiRequest<ExcelImportResult>('/excel/import/stock/', {
    //   method: 'POST',
    //   body: formData,
    //   headers: {}, // Don't set Content-Type for FormData
    // });
    throw new Error('API not implemented yet');
  },

  importClientes: async (_file: File): Promise<ExcelImportResult> => {
    // TODO: Uncomment when backend is ready
    // const formData = new FormData();
    // formData.append('file', file);
    // return apiRequest<ExcelImportResult>('/excel/import/clientes/', {
    //   method: 'POST',
    //   body: formData,
    //   headers: {}, // Don't set Content-Type for FormData
    // });
    throw new Error('API not implemented yet');
  },

  importProveedores: async (_file: File): Promise<ExcelImportResult> => {
    // TODO: Uncomment when backend is ready
    // const formData = new FormData();
    // formData.append('file', file);
    // return apiRequest<ExcelImportResult>('/excel/import/proveedores/', {
    //   method: 'POST',
    //   body: formData,
    //   headers: {}, // Don't set Content-Type for FormData
    // });
    throw new Error('API not implemented yet');
  },
};

// ============================================================================
// COMPANY CONFIGURATION API
// ============================================================================

/**
 * Company/Business configuration
 * TODO: [BACKEND] Implement these endpoints
 */

export interface CompanyConfig {
  id: number;
  razonSocial: string;
  nombreFantasia?: string;
  cuit: string;
  domicilio: string;
  localidad: string;
  provincia: string;
  codigoPostal: string;
  telefono?: string;
  email?: string;
  condicionIVA: string;
  ingresosBrutos?: string;
  inicioActividades: string;
  logoUrl?: string;
}

export const companyAPI = {
  /**
   * Configuración de empresa (read-only). GET /api/empresa-config/
   */
  getConfig: async (): Promise<CompanyConfig | null> => {
    try {
      return await apiRequest<CompanyConfig>('/empresa-config/');
    } catch (e: unknown) {
      const status = (e as { status?: number })?.status;
      if (status === 404) return null;
      throw e;
    }
  },

  /**
   * Update company configuration
   * TODO: [BACKEND] Implement this endpoint
   * 
   * BACKEND REQUIREMENTS:
   * 1. Endpoint: PATCH /api/company/config/
   * 2. Authentication: Required
   * 3. Authorization: Only admin users
   * 4. Clear cache after update
   */
  updateConfig: async (_data: Partial<CompanyConfig>): Promise<CompanyConfig> => {
    // TODO: Uncomment when backend is ready
    // return apiRequest<CompanyConfig>('/company/config/', {
    //   method: 'PATCH',
    //   body: JSON.stringify(data),
    // });
    throw new Error('API not implemented yet');
  },
};

// ============================================================================
// NOTIFICATIONS API
// ============================================================================

export interface BackendNotification {
  id: number;
  user?: number;
  title: string;
  message: string;
  type: string;
  severity: 'high' | 'medium' | 'low';
  read: boolean;
  data?: any;
  module_route?: string;
  created_at: string;
}

export const notificationsAPI = {
  list: async (userId?: number, read?: boolean): Promise<BackendNotification[]> => {
    let url = '/notifications/';
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId.toString());
    if (read !== undefined) params.append('read', read.toString());

    // Sort by newest first default ordering is handled by backend but good to know
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;

    const response = await apiRequest<any>(url);
    // Handle paginated response (has .results) or direct array
    if (response && Array.isArray(response.results)) {
      return response.results;
    }
    if (Array.isArray(response)) {
      return response;
    }
    return [];
  },

  markRead: async (id: number): Promise<void> => {
    return apiRequest<void>(`/notifications/${id}/mark_read/`, {
      method: 'POST',
    });
  },

  markAllRead: async (userId?: number): Promise<void> => {
    return apiRequest<void>('/notifications/mark_all_read/', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  },
};

// ============================================================================
// SYSTEM CONFIG API (Global Configuration Storage)
// ============================================================================

export interface ExcelImportConfig {
  mapping: {
    codigo: string;
    nombre: string;
    proveedor: string;
    grupo: string;
    subgrupo: string;
    cantidad: string;
    sucursal: string;
    deposito: string;
    precioBase: string;
    precioLista: string;
    iva: string;
  };
  startRow: number;
}

export interface NotificationConfig {
  enabledTypes: {
    stockCero: boolean;
    stockBajo: boolean;
    facturaPendiente: boolean;
    remitoPendiente: boolean;
    pedidoPendiente: boolean;
  };
  lowStockThreshold: number;
}

export const systemConfigAPI = {
  /**
   * Get a configuration by key.
   * Returns empty object if key doesn't exist.
   */
  getConfig: async <T = Record<string, unknown>>(key: string): Promise<T> => {
    return apiRequest<T>(`/system-config/by-key/${key}/`);
  },

  /**
   * Set a configuration by key (upsert - creates or updates).
   */
  setConfig: async <T = Record<string, unknown>>(key: string, value: T, description?: string): Promise<{ key: string; value: T; created: boolean }> => {
    return apiRequest<{ key: string; value: T; created: boolean }>(`/system-config/by-key/${key}/`, {
      method: 'POST',
      body: JSON.stringify({ value, description }),
    });
  },

  /**
   * Get Excel import configuration specifically.
   */
  getExcelImportConfig: async (): Promise<ExcelImportConfig | null> => {
    const result = await apiRequest<ExcelImportConfig>('/system-config/by-key/excel_import_config/');
    // API returns empty object if not found
    if (!result || !result.mapping) return null;
    return result;
  },

  /**
   * Save Excel import configuration.
   */
  setExcelImportConfig: async (config: ExcelImportConfig): Promise<void> => {
    await apiRequest('/system-config/by-key/excel_import_config/', {
      method: 'POST',
      body: JSON.stringify({
        value: config,
        description: 'Excel import column mapping and start row configuration'
      }),
    });
  },

  /**
   * Get Notification configuration.
   */
  getNotificationConfig: async (): Promise<NotificationConfig | Record<string, unknown> | null> => {
    const result = await apiRequest<Record<string, unknown>>(
      "/system-config/by-key/notification_config/"
    );
    if (!result || typeof result !== "object") return null;
    // Accept camelCase or snake_case from JSONField (renderer may vary)
    const r = result as Record<string, unknown>;
    if (!r.enabledTypes && !r.enabled_types) {
      return null;
    }
    return result as NotificationConfig;
  },

  /**
   * Save Notification configuration.
   */
  setNotificationConfig: async (config: NotificationConfig): Promise<void> => {
    await apiRequest('/system-config/by-key/notification_config/', {
      method: 'POST',
      body: JSON.stringify({
        value: config,
        description: 'System-wide notification enabled types and thresholds'
      }),
    });
  },

  /**
   * DANGEROUS (demo/dev): wipe local de facturas/NC/CAE, remitos, recibos,
   * pagos a proveedor (REC-PROV), presupuestos, caja/cierres, cheques,
   * notificaciones, notas de clientes (ClienteNota), registro de actividad
   * (activity_logs) y saldos. Numeradores locales a 0 (incl. REC-PROV).
   * No toca productos/stock, maestros de clientes ni configurador.
   */
  resetTestData: async (): Promise<{
    status: string;
    message: string;
    deleted?: Record<string, number>;
    numeraciones_reset?: number;
    clientes_saldo_zeroed?: number;
    warning?: string;
  }> => {
    return apiRequest('/system-config/reset-test-data/', {
      method: 'POST',
    });
  },

  /**
   * Borra stock y movimientos; por defecto también todos los productos y reinicia PK (Postgres/SQLite).
   * wipeProductos: false → solo tablas stock + movimientos.
   */
  resetStock: async (opts?: { wipeProductos?: boolean }): Promise<{
    status: string;
    message: string;
    wipe_productos?: boolean;
    deleted_product_rows?: number;
    deleted_stock_rows?: number;
    deleted_movimiento_rows?: number;
  }> => {
    return apiRequest('/system-config/reset-stock/', {
      method: 'POST',
      body: JSON.stringify({
        wipe_productos: opts?.wipeProductos !== false,
      }),
    });
  },

  /**
   * Listas para validación de datos en Excel (CSV/TXT). No usa apiRequest porque la respuesta es attachment, no JSON.
   */
  downloadExportLists: async (output: 'csv' | 'text'): Promise<void> => {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const url = `${API_BASE_URL}/system-config/export-lists/?output=${output}`;
    const response = await fetch(url, { method: 'GET', headers, cache: 'no-store' });
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text.slice(0, 300) || response.statusText);
    }
    const blob = await response.blob();
    const dispo = response.headers.get('Content-Disposition');
    let filename = output === 'csv' ? 'listas_excel.csv' : 'listas_excel.txt';
    const m = dispo && /filename="?([^";]+)"?/i.exec(dispo);
    if (m) filename = m[1].trim();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  },
};

// ============================================================================
// BACKUP LIST TYPE (usado también en setup first-run)
// ============================================================================

export interface BackupInfo {
  filename: string;
  path?: string;
  size: number;
  /** DRF camelCase */
  createdAt?: string | null;
  created_at?: string | null;
  label: string;
  name: string;
  components: string[];
  sha256?: string;
  gitCommit?: string;
  git_commit?: string;
  pdfTemplateCount?: number;
  pdf_template_count?: number;
  /** manual | scheduled — backups antiguos sin campo se tratan como manual */
  type?: 'manual' | 'scheduled';
}

export interface BackupRetentionSettings {
  keepLast?: number;
  keep_last: number;
  keepDays?: number;
  keep_days: number;
}

export interface BackupSchedule {
  id: number;
  name: string;
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'hourly';
  hour: number;
  minute: number;
  everyHours?: number;
  every_hours?: number;
  weekday?: number | null;
  label?: string;
  lastRunAt?: string | null;
  last_run_at?: string | null;
  lastStatus?: string;
  last_status?: string;
  lastMessage?: string;
  last_message?: string;
}

// ============================================================================
// ARCA / AFIP — configuración fiscal (Opciones + first-run sin JWT)
// ============================================================================

export type ArcaEnvironment = 'homologacion' | 'produccion';

export interface ArcaAfipEnvSnapshot {
  puntoVenta: number;
  cuit: string;
  certPath: string;
  privateKeyPath: string;
  certPresent: boolean;
  keyPresent: boolean;
  usingUploadedCert: boolean;
  certNotAfter?: string | null;
  taCachePath: string;
  isProduction: boolean;
}

export interface ArcaAfipStatusResponse {
  config: Record<string, unknown>;
  activeEnvironment: string;
  environments: {
    homologacion: ArcaAfipEnvSnapshot;
    produccion: ArcaAfipEnvSnapshot;
  };
}

export const arcaAfipAPI = {
  status: async (
    params?: { cuit?: string },
    requestOpts?: { omitAuth?: boolean }
  ): Promise<ArcaAfipStatusResponse> => {
    return apiRequest<ArcaAfipStatusResponse>('/opciones/arca-afip/status/', {
      params: params?.cuit ? { cuit: params.cuit } : undefined,
      omitAuth: requestOpts?.omitAuth,
    });
  },

  patchSettings: async (
    body: Record<string, unknown>,
    requestOpts?: { omitAuth?: boolean }
  ): Promise<{ ok: boolean; config: Record<string, unknown> }> => {
    return apiRequest('/opciones/arca-afip/settings/', {
      method: 'PATCH',
      body: JSON.stringify(body),
      omitAuth: requestOpts?.omitAuth,
    });
  },

  uploadCertificate: async (
    environment: ArcaEnvironment,
    certificate: File,
    privateKey: File,
    requestOpts?: { omitAuth?: boolean }
  ): Promise<{ ok: boolean; environment?: string; error?: string }> => {
    const formData = new FormData();
    formData.append('environment', environment);
    formData.append('certificate', certificate);
    formData.append('private_key', privateKey);
    const headers: HeadersInit = {};
    if (!requestOpts?.omitAuth) {
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE_URL}/opciones/arca-afip/upload-certificate/`, {
      method: 'POST',
      headers,
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return { ok: false, error: (data as { error?: string }).error || response.statusText };
    }
    return data as { ok: boolean; environment?: string };
  },

  validate: async (
    body: { environment?: ArcaEnvironment; cuit?: string },
    requestOpts?: { omitAuth?: boolean }
  ): Promise<{
    ok: boolean;
    message?: string;
    error?: string;
    environment?: string;
    cuit?: string;
  }> => {
    return apiRequest('/opciones/arca-afip/validate/', {
      method: 'POST',
      body: JSON.stringify(body),
      omitAuth: requestOpts?.omitAuth,
    });
  },

  puntosVenta: async (
    params: { environment: ArcaEnvironment; cuit?: string },
    requestOpts?: { omitAuth?: boolean }
  ): Promise<{
    ok: boolean;
    puntosVenta?: Array<{
      nro?: number;
      emisionTipo?: string;
      bloqueado?: string;
      fchBaja?: string;
    }>;
    wsfeErrors?: Array<{ code?: number; msg?: string }>;
    error?: string;
  }> => {
    return apiRequest(`/opciones/arca-afip/puntos-venta/`, {
      params: {
        environment: params.environment,
        ...(params.cuit ? { cuit: params.cuit } : {}),
      },
      omitAuth: requestOpts?.omitAuth,
    });
  },
};

// ============================================================================
// SETUP / FIRST-RUN API
// ============================================================================

/** Matches DRF camelCase JSON (djangorestframework-camel-case). */
export interface SetupStatus {
  needsBootstrap: boolean;
  hasUsers: boolean;
  hasRoles: boolean;
  hasSucursales: boolean;
  hasDepositos: boolean;
  hasPuntosVenta: boolean;
  hasMetodosPago: boolean;
}

/** Payload for first-run wizard (camelCase; backend maps empresa fields). */
export interface WizardBootstrapPayload {
  adminUsername: string;
  adminPassword: string;
  adminEmail?: string;
  company: {
    razonSocial: string;
    nombreFantasia?: string;
    cuit: string;
    domicilio: string;
    localidad: string;
    provincia: string;
    codigoPostal?: string;
    telefono?: string;
    email?: string;
    condicionIva?: string;
    ingresosBrutos?: string;
    inicioActividades: string;
    logoUrl?: string;
  };
  /** Sucursal y depósito principal (datos de negocio); códigos opcionales (default SUC001 / DEP001). */
  primarySucursal: { nombre: string; codigo?: string };
  primaryDeposito: { nombre: string; codigo?: string };
}

export const setupAPI = {
  getStatus: async (): Promise<SetupStatus> => {
    return apiRequest<SetupStatus>('/setup/status/');
  },
  /** Sin cuerpo: bootstrap con variables de entorno (CI / legado). */
  bootstrap: async (): Promise<{ status: string; output: string }> => {
    return apiRequest<{ status: string; output: string }>('/setup/bootstrap/', { method: 'POST', body: '{}' });
  },
  wizardBootstrap: async (payload: WizardBootstrapPayload): Promise<{ status: string; output: string; message?: string }> => {
    return apiRequest('/setup/bootstrap/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  bootstrapWithConfig: async (data: { company?: Record<string, unknown> }): Promise<{ status: string; output: string }> => {
    return apiRequest('/setup/bootstrap-with-config/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  /** Solo con sistema sin roles (first-run). */
  listAvailableBackups: async (): Promise<BackupInfo[]> => {
    return apiRequest<BackupInfo[]>('/setup/available-backups/');
  },
  restoreFromBackup: async (filename: string, confirm: string): Promise<{ status: string; output: string; message?: string }> => {
    return apiRequest('/setup/restore-from-backup/', {
      method: 'POST',
      body: JSON.stringify({ filename, confirm }),
    });
  },
};

// ============================================================================
// BACKUP API
// ============================================================================

export interface BackupJobStatus {
  id: string;
  kind?: 'manual' | 'scheduled' | string;
  label?: string;
  schedule_id?: number | null;
  scheduleId?: number | null;
  status: 'queued' | 'running' | 'success' | 'error' | 'skipped' | string;
  stage?: string;
  stage_label?: string;
  stageLabel?: string;
  progress?: number;
  message?: string;
  backup?: { path?: string; name?: string; sha256?: string; type?: string; status?: string } | null;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
}

export type BackupCreateResponse =
  | {
      status: 'accepted';
      job_id?: string;
      jobId?: string;
      job?: BackupJobStatus;
    }
  | {
      status: 'success';
      backup: { path: string; name: string; sha256: string; type?: string };
      output?: string;
      job_id?: string;
      jobId?: string;
    };

export const backupAPI = {
  list: async (type?: 'manual' | 'scheduled'): Promise<BackupInfo[]> => {
    return apiRequest<BackupInfo[]>('/backups/', {
      params: type ? { type } : undefined,
    });
  },
  /** Async by default (202 + job_id). Pass sync:true for blocking create. */
  create: async (label?: string, opts?: { sync?: boolean }): Promise<BackupCreateResponse> => {
    return apiRequest('/backups/', {
      method: 'POST',
      body: JSON.stringify({ label: label || '', ...(opts?.sync ? { sync: true } : {}) }),
      params: opts?.sync ? { sync: '1' } : undefined,
    });
  },
  getJob: async (jobId: string): Promise<BackupJobStatus> => {
    return apiRequest<BackupJobStatus>(`/backups/jobs/${encodeURIComponent(jobId)}/`);
  },
  get: async (filename: string): Promise<BackupInfo> => {
    return apiRequest<BackupInfo>(`/backups/${encodeURIComponent(filename)}/`);
  },
  remove: async (filename: string): Promise<{ status: string }> => {
    return apiRequest(`/backups/${encodeURIComponent(filename)}/`, { method: 'DELETE' });
  },
  restore: async (
    filename: string,
    mode: 'full' | 'templates' | 'db' | 'media' = 'full',
  ): Promise<{ status: string; output: string; message?: string }> => {
    return apiRequest(`/backups/${encodeURIComponent(filename)}/restore/`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
    });
  },
  getLog: async (
    filename: string,
  ): Promise<{
    filename: string;
    available: boolean;
    has_create_log?: boolean;
    hasCreateLog?: boolean;
    has_restore_log?: boolean;
    hasRestoreLog?: boolean;
    /** Structured sections (create + restores). Prefer this over flat `log`. */
    entries?: Array<{
      id: string;
      title: string;
      status: 'ok' | 'fail' | 'unknown' | string;
      timestamp?: string | null;
      mode?: string | null;
      body: string;
    }>;
    /** Flat combined text — legacy / compat. */
    log?: string;
  }> => {
    return apiRequest(`/backups/${encodeURIComponent(filename)}/log/`);
  },
  download: async (filename: string): Promise<void> => {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE_URL}/backups/${encodeURIComponent(filename)}/download/`, {
      method: 'GET',
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  getRetention: async (): Promise<BackupRetentionSettings> => {
    return apiRequest<BackupRetentionSettings>('/backups/retention/');
  },
  setRetention: async (settings: { keep_last?: number; keep_days?: number }): Promise<BackupRetentionSettings> => {
    return apiRequest<BackupRetentionSettings>('/backups/retention/', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  },
  applyRetention: async (): Promise<{ status: string; deleted: Array<{ filename: string; type: string }>; kept: string[] }> => {
    return apiRequest('/backups/retention/apply/', { method: 'POST' });
  },
};

export const backupScheduleAPI = {
  list: async (): Promise<BackupSchedule[]> => {
    return apiRequest<BackupSchedule[]>('/backup-schedules/');
  },
  create: async (data: {
    name: string;
    frequency: 'daily' | 'weekly' | 'hourly';
    hour?: number;
    minute?: number;
    every_hours?: number;
    weekday?: number | null;
    label?: string;
    enabled?: boolean;
  }): Promise<BackupSchedule> => {
    return apiRequest<BackupSchedule>('/backup-schedules/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id: number, data: Partial<{
    name: string;
    frequency: 'daily' | 'weekly' | 'hourly';
    hour: number;
    minute: number;
    every_hours: number;
    weekday: number | null;
    label: string;
    enabled: boolean;
  }>): Promise<BackupSchedule> => {
    return apiRequest<BackupSchedule>(`/backup-schedules/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
  remove: async (id: number): Promise<{ status: string }> => {
    return apiRequest(`/backup-schedules/${id}/`, { method: 'DELETE' });
  },
  /** Async by default (202 + job_id). Pass sync:true for blocking run. */
  runNow: async (
    id: number,
    opts?: { sync?: boolean },
  ): Promise<
    BackupSchedule & {
      status?: string;
      job_id?: string;
      jobId?: string;
      job?: BackupJobStatus;
      schedule?: BackupSchedule;
    }
  > => {
    return apiRequest(`/backup-schedules/${id}/run-now/`, {
      method: 'POST',
      body: opts?.sync ? JSON.stringify({ sync: true }) : '{}',
      params: opts?.sync ? { sync: '1' } : undefined,
    });
  },
};

// ============================================================================
// PDF TEMPLATE API (Editor Comprobantes)
// ============================================================================

function pdfTemplateConfigKey(documentTypeOrKey: string): string {
  return documentTypeOrKey.startsWith('pdf_template_')
    ? documentTypeOrKey
    : `pdf_template_${documentTypeOrKey}`;
}

export const pdfTemplateAPI = {
  listTemplates: async (): Promise<Array<{ key: string; description: string; has_content: boolean; nombre?: string }>> => {
    return apiRequest<Array<{ key: string; description: string; has_content: boolean; nombre?: string }>>('/system-config/pdf-templates/');
  },

  /**
   * Get PDF template by document type (`factura_a`) or full key (`pdf_template_factura_a`).
   * Returns null if not found or invalid.
   */
  getTemplate: async (documentTypeOrKey: string): Promise<Record<string, unknown> | null> => {
    const key = pdfTemplateConfigKey(documentTypeOrKey);
    const result = await apiRequest<Record<string, unknown>>(`/system-config/by-key/${key}/`);
    if (!result || typeof result !== 'object' || !('basePdf' in result) || !('schemas' in result)) {
      return null;
    }
    return result;
  },

  /**
   * Get a pdfme-compatible template.
   * @param blank - if true, returns blank base (no ReportLab design) with margin guides
   * @param pageSize - when blank: 'a4' | 'a3' | 'letter'
   */
  getFromReportLab: async (documentType: string, blank = false, pageSize = 'a4'): Promise<Record<string, unknown> | null> => {
    const result = await apiRequest<Record<string, unknown>>(
      `/opciones/export-reportlab-pdf-template/`,
      { params: { doc_type: documentType, ...(blank ? { blank: '1', page_size: pageSize } : {}) } }
    );
    if (!result || typeof result !== 'object' || !('basePdf' in result) || !('schemas' in result)) {
      return null;
    }
    return result;
  },

  /** Get blank template - no ReportLab, guaranteed empty white pages. */
  getBlankTemplate: async (pageSize = 'a4', numPages = 1): Promise<Record<string, unknown> | null> => {
    const result = await apiRequest<{ basePdf: string; schemas: unknown[] }>(
      `/opciones/blank-pdf-base/`,
      { params: { page_size: pageSize, num_pages: numPages } }
    );
    if (!result?.basePdf || !Array.isArray(result.schemas)) return null;
    return result;
  },

  /**
   * Duplicate the last page of a template (for Original/Duplicado).
   */
  duplicatePage: async (template: Record<string, unknown>): Promise<Record<string, unknown> | null> => {
    const result = await apiRequest<Record<string, unknown>>('/opciones/duplicate-pdf-template-page/', {
      method: 'POST',
      body: JSON.stringify(template),
    });
    return result ?? null;
  },

  addBlankPage: async (template: Record<string, unknown>): Promise<Record<string, unknown> | null> => {
    const result = await apiRequest<Record<string, unknown>>('/opciones/add-blank-pdf-template-page/', {
      method: 'POST',
      body: JSON.stringify(template),
    });
    return result ?? null;
  },

  removeLastPage: async (template: Record<string, unknown>): Promise<Record<string, unknown> | null> => {
    const result = await apiRequest<Record<string, unknown>>('/opciones/remove-last-pdf-template-page/', {
      method: 'POST',
      body: JSON.stringify(template),
    });
    return result ?? null;
  },

  /**
   * Update (upsert) PDF template by document type or full key.
   */
  saveTemplate: async (
    documentTypeOrKey: string,
    template: Record<string, unknown>,
    description?: string
  ): Promise<{ key: string; value: Record<string, unknown>; created: boolean }> => {
    const key = pdfTemplateConfigKey(documentTypeOrKey);
    const suffix = key.startsWith('pdf_template_') ? key.slice('pdf_template_'.length) : documentTypeOrKey;
    return apiRequest<{ key: string; value: Record<string, unknown>; created: boolean }>(
      `/system-config/by-key/${key}/`,
      {
        method: 'POST',
        body: JSON.stringify({
          value: template,
          description: description ?? `Plantilla PDF para ${suffix}`,
        }),
      }
    );
  },

  /**
   * Create a new PDF template key. Fails with 409 if the key already exists (no overwrite).
   */
  createTemplate: async (
    key: string,
    template: Record<string, unknown>,
    description: string
  ): Promise<{ key: string; value: Record<string, unknown>; created: boolean }> => {
    const configKey = pdfTemplateConfigKey(key);
    return apiRequest<{ key: string; value: Record<string, unknown>; created: boolean }>(
      `/system-config/by-key/${configKey}/`,
      {
        method: 'POST',
        params: { create_only: '1' },
        body: JSON.stringify({
          value: template,
          description,
        }),
      }
    );
  },
};